import { Authenticator, ThemeProvider, Theme, View, Heading, Text, Button, useAuthenticator } from '@aws-amplify/ui-react';
import { I18n, Hub } from 'aws-amplify/utils';
import '@aws-amplify/ui-react/styles.css';
import { useState, useEffect, ReactNode } from 'react';
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import AvisoSessaoExpirada from './components/AvisoSessaoExpirada';
import { claimsDoJwt } from './utils/guardaSessao';
import { API_URL } from './config';
import { useTabsPrefs } from './hooks/useTabsPrefs';
import {
  BrowserRouter, Routes, Route, Navigate, NavLink as RouterNavLink,
  useNavigate, useLocation, useParams, matchPath,
} from 'react-router-dom';
// Mantine — shell (sidebar vertical colapsável + header) que substituiu o
// antigo nav horizontal + abas. Páginas internas continuam com estilos próprios
// (migração em ondas: shell primeiro, telas depois).
import { AppShell, Group, Burger, NavLink as MantineNavLink, ScrollArea, Badge, Menu, Avatar, UnstyledButton } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconHome, IconChartBar, IconBriefcase, IconSettings, IconBox,
  IconCircleCheck, IconLock, IconLogout, IconChevronRight, IconUser, IconChevronDown,
  IconBrandInstagram, IconScale,
  // Ícones das PÁGINAS (2026-08-21). Antes o menu misturava emoji colorido no
  // meio do rótulo com páginas sem ícone nenhum — três estilos na mesma lista.
  IconChartPie, IconCash, IconReceipt, IconTrendingUp, IconBuildingBank,
  IconAdjustments, IconChecklist, IconChartFunnel, IconPercentage,
  IconBuilding, IconFileDescription, IconGavel, IconRobot,
  IconFileText, IconTent, IconCalendarEvent, IconUsersGroup, IconClock, IconRadar, IconMailForward,
  IconBuildingWarehouse, IconPackages, IconTicket, IconFolders,
  IconNews, IconInbox, IconSpeakerphone, IconShieldLock, IconSchool,
  IconKey, IconPlugConnected, IconListCheck, IconCloud, IconDatabase,
  IconIdBadge2, IconUserSearch, IconClockCheck, IconTargetArrow,
} from '@tabler/icons-react';
// FIN-HUBS (2026-07-13): Dashboard, Profissionais, RateioMensalClockify,
// Diferimentos, LancamentosManuais, Orcamento, CentrosCusto, Categorias e
// Emprestimos saíram daqui — agora são importados pelos hubs financeiros.
import Patrimonio from './pages/Patrimonio';
import Admin from './pages/Admin';
import Nexo from './pages/nexo/Nexo';
import { useNexoAcesso } from './hooks/useNexoAcesso';
import Atividades from './pages/Atividades';
import AtividadesRequisicoes from './pages/AtividadesRequisicoes';
import AtividadesReembolsos from './pages/AtividadesReembolsos';
import AtividadesOuvidoria from './pages/AtividadesOuvidoria';
import AdministrativoTickets from './pages/AdministrativoTickets';
import AdministrativoExpedientes from './pages/AdministrativoExpedientes';
import AdministrativoDocumentos from './pages/AdministrativoDocumentos';
import AdministrativoRHHub from './pages/administrativo-rh/AdministrativoRHHub';
import JuridicoContratos from './pages/JuridicoContratos';
import EditorExpediente from './pages/administrativo-expedientes/EditorExpediente';
import MeuPerfil from './pages/MeuPerfil';
import Marketing from './pages/Marketing';
import AdminHierarquia from './pages/AdminHierarquia';
import AdminIntegracoes from './pages/AdminIntegracoes';
import ComercialPipeline from './pages/ComercialPipeline';
import ComercialLeads from './pages/ComercialLeads';
// COM-HUBS (2026-08-21): as outras 11 telas do Comercial entram pelos hubs
// (./pages/comercial-hubs/), não mais uma a uma.
import HomeDashboard from './pages/HomeDashboard';
import PaginaPublicaProposta from './pages/PaginaPublicaProposta';
import PaginaPublicaCliente from './pages/PaginaPublicaCliente';
import PaginaPublicaBm from './pages/PaginaPublicaBm';
import PaginaPublicaDrive from './pages/PaginaPublicaDrive';
import PaginaPublicaPlanejamento from './pages/PaginaPublicaPlanejamento';
import PaginaPublicaEstacao from './pages/PaginaPublicaEstacao';
import HydraDrawer from './components/HydraDrawer';
import JimDrawer from './components/JimDrawer';
import PamDrawer from './components/PamDrawer';
import NotificacoesBell from './components/NotificacoesBell';
import HorasAtalho, { EVENTO_ABRIR_ATALHO } from './components/HorasAtalho';
import HorasBomDia from './components/HorasBomDia';
import BannerAtualizacaoSW from './components/BannerAtualizacaoSW';
import HydraConfig from './pages/HydraConfig';
import { useIsMobile } from './hooks/useResponsive';
import OperacionalContratos from './pages/OperacionalContratos';
import { PermsProvider, usePerms } from './hooks/usePerms';
import JimConfig from './pages/JimConfig';
import Estoque from './pages/Estoque';
import PamConfig from './pages/PamConfig';
import FinanceiroExtratoBancario from './pages/FinanceiroExtratoBancario';
import OperacionalDiarioCampo from './pages/OperacionalDiarioCampo';
import OperacionalDadosCampo from './pages/OperacionalDadosCampo';
import OperacionalReunioes from './pages/OperacionalReunioes';
import OperacionalReuniaoGeral from './pages/OperacionalReuniaoGeral';
import OperacionalControleEntregas from './pages/OperacionalControleEntregas';
import PlanejamentoCampo from './pages/PlanejamentoCampo';
import KitsCampo from './pages/KitsCampo';
import OperacionalCampoHub from './pages/OperacionalCampoHub';
import OperacionalMonitoramentoHub from './pages/OperacionalMonitoramentoHub';
import Horas from './pages/operacional-horas';
import HorasDaEquipe from './pages/administrativo-horas';
import PlanoDeAcao from './pages/administrativo-plano-acao';
import AdminCodigosConvite from './pages/AdminCodigosConvite';
import AdminPermissoes from './pages/AdminPermissoes';
import AdminDadosTecnicos from './pages/AdminDadosTecnicos';
// Onda 0 (2026-07-02): FinanceiroReconcileNotion aposentado — rota redireciona pra Admin > Integrações
import FinanceiroFluxoCaixa from './pages/FinanceiroFluxoCaixa';
// COM-HUBS (2026-08-21): menu Comercial agrupado em 5 hubs com abas.
import ComercialPainelHub from './pages/comercial-hubs/ComercialPainelHub';
import ComercialClientesHub from './pages/comercial-hubs/ComercialClientesHub';
import ComercialPropostasHub from './pages/comercial-hubs/ComercialPropostasHub';
import ComercialLicitacoesHub from './pages/comercial-hubs/ComercialLicitacoesHub';
import ComercialComissoesHub from './pages/comercial-hubs/ComercialComissoesHub';
import ComercialRadarHub from './pages/comercial-hubs/ComercialRadarHub';
import ComercialFollowupsHub from './pages/comercial-hubs/ComercialFollowupsHub';
// FIN-HUBS (2026-07-13): menu Financeiro agrupado em 4 hubs temáticos com abas.
// As páginas individuais são importadas pelos hubs; as rotas antigas viram
// redirects (RedirectHub) que preservam a query string (deep-links Onda 0).
import FinanceiroDreHub from './pages/financeiro-hubs/FinanceiroDreHub';
import FinanceiroRecebimentosHub from './pages/financeiro-hubs/FinanceiroRecebimentosHub';
import FinanceiroPagamentosHub from './pages/financeiro-hubs/FinanceiroPagamentosHub';
import FinanceiroCadastrosHub from './pages/financeiro-hubs/FinanceiroCadastrosHub';

// ─── Cores Hydrodata ─────────────────────────────────────────
const HD_BLUE_DEEP = '#020D7F';
const HD_BLUE = '#0930D0';
const HD_BG = '#F7F7F7';

// ─── Tradução para PT-BR do Authenticator ────────────────────
I18n.putVocabularies({
  'pt-BR': {
    'Sign In': 'Entrar',
    'Sign in': 'Entrar',
    'Sign in to your account': 'Acesse sua conta',
    'Signing in': 'Entrando',
    'Sign Out': 'Sair',
    'Email': 'E-mail',
    'Enter your Email': 'Informe seu e-mail',
    'Password': 'Senha',
    'Enter your Password': 'Informe sua senha',
    'Please confirm your Password': 'Confirme sua senha',
    'Confirm Password': 'Confirmar senha',
    'Forgot your password?': 'Esqueceu sua senha?',
    'Forgot Password?': 'Esqueceu a senha?',
    'Reset Password': 'Redefinir senha',
    'Reset your Password': 'Redefina sua senha',
    'Enter your email': 'Informe seu e-mail',
    'Send code': 'Enviar código',
    'Send Code': 'Enviar código',
    'Back to Sign In': 'Voltar para o login',
    'Change Password': 'Alterar senha',
    'Confirm': 'Confirmar',
    'Confirmation Code': 'Código de confirmação',
    'Code': 'Código',
    'New Password': 'Nova senha',
    'Submit': 'Enviar',
    'Incorrect username or password.': 'E-mail ou senha incorretos.',
    'User does not exist.': 'Usuário não cadastrado.',
    'Password does not conform to policy': 'A senha não atende aos requisitos',
    'Password did not conform with policy: Password not long enough': 'Senha muito curta (mínimo 8 caracteres)',
    'Username cannot be empty': 'Informe o e-mail',
    'Password cannot be empty': 'Informe a senha',
    'Network error': 'Erro de conexão',
    'Your passwords must match': 'As senhas não conferem',
    'We Emailed You': 'Enviamos um e-mail',
    'Your code is on the way. To log in, enter the code we emailed to': 'O código está a caminho. Para entrar, digite o código enviado para',
    'It may take a minute to arrive.': 'Pode levar alguns minutos pra chegar.',
    'New password': 'Nova senha',
    'Enter your new password': 'Digite sua nova senha',
    'Enter your code': 'Digite o código recebido',
    'Enter your new Password': 'Digite sua nova senha',
    'Verify': 'Verificar',
    'Verifying': 'Verificando',
    'Resend Code': 'Reenviar código',
  },
});
I18n.setLanguage('pt-BR');

// ─── Tema do Authenticator (Hydrodata) ───────────────────────
const authTheme: Theme = {
  name: 'hydrodata-theme',
  tokens: {
    colors: {
      brand: {
        primary: {
          10:  { value: '#E7ECFD' },
          20:  { value: '#C5D0F8' },
          40:  { value: '#7A95EE' },
          60:  { value: '#2F56DD' },
          80:  { value: HD_BLUE },
          90:  { value: '#061DA0' },
          100: { value: HD_BLUE_DEEP },
        },
      },
      background: {
        primary: { value: '#ffffff' },
        secondary: { value: HD_BG },
      },
      font: {
        interactive: { value: HD_BLUE },
      },
    },
    components: {
      button: {
        primary: {
          backgroundColor: { value: HD_BLUE },
          _hover: { backgroundColor: { value: HD_BLUE_DEEP } },
          _focus: { backgroundColor: { value: HD_BLUE_DEEP } },
        },
        link: {
          color: { value: HD_BLUE },
          _hover: { color: { value: HD_BLUE_DEEP }, backgroundColor: { value: '#E7ECFD' } },
          _focus: { color: { value: HD_BLUE_DEEP }, backgroundColor: { value: '#E7ECFD' } },
          _active: { color: { value: HD_BLUE_DEEP } },
        },
      },
      fieldcontrol: {
        _focus: { borderColor: { value: HD_BLUE } },
      },
      tabs: {
        item: {
          _active: { color: { value: HD_BLUE }, borderColor: { value: HD_BLUE } },
          _hover: { color: { value: HD_BLUE_DEEP } },
        },
      },
    },
  },
};

// ─── Componentes customizados do Authenticator ───────────────
const authComponents = {
  Header() {
    // [e07e7efc] Header redesenhado: logo + título com hierarquia tipográfica
    // mais clara, divider sutil. O ornamento de fundo já vem do CSS global.
    return (
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        padding: '40px 24px 22px', textAlign: 'center',
        borderBottom: '0.5px solid #F0F1F5',
      }}>
        <img
          alt="Hydrodata"
          src="/hydrodata-logo-sm-azul-140.png"
          style={{ height: 60, width: 'auto', maxWidth: 'none', objectFit: 'contain', marginBottom: 6 }}
        />
        <Heading level={3} color={HD_BLUE_DEEP} marginTop="16px" fontWeight="600"
          style={{ fontSize: 20, letterSpacing: '-0.01em' }}>
          Gestão Empresarial
        </Heading>
        <Text color="#6A7092" fontSize="12px" marginTop="6px"
          style={{ letterSpacing: '0.02em' }}>
          Bem-vindo(a) de volta
        </Text>
      </div>
    );
  },
  Footer() {
    return (
      <View textAlign="center" padding="16px 0 24px">
        <Text color="#9ca3af" fontSize="11px">
          © {new Date().getFullYear()} Hydrodata Engenharia · Todos os direitos reservados
        </Text>
      </View>
    );
  },
  SignIn: {
    Footer() {
      const { toForgotPassword } = useAuthenticator();
      return (
        <View textAlign="center" padding="0 0 16px">
          <Button
            fontWeight="normal"
            onClick={toForgotPassword}
            size="small"
            variation="link"
            style={{ color: HD_BLUE, fontSize: 12 }}
          >
            Esqueci minha senha
          </Button>
        </View>
      );
    },
  },
  ForgotPassword: {
    Header() {
      return (
        <Heading level={5} padding="16px 0 0" color={HD_BLUE_DEEP} textAlign="center" fontWeight="500">
          Redefinir senha
        </Heading>
      );
    },
  },
  ConfirmResetPassword: {
    Header() {
      return (
        <Heading level={5} padding="16px 0 0" color={HD_BLUE_DEEP} textAlign="center" fontWeight="500">
          Defina sua nova senha
        </Heading>
      );
    },
  },
  ConfirmSignIn: {
    Header() {
      return (
        <Heading level={5} padding="16px 0 0" color={HD_BLUE_DEEP} textAlign="center" fontWeight="500">
          Confirme o acesso
        </Heading>
      );
    },
  },
  ForceNewPassword: {
    Header() {
      return (
        <View padding="16px 0 0" textAlign="center">
          <Heading level={5} color={HD_BLUE_DEEP} fontWeight="500">
            Primeiro acesso
          </Heading>
          <Text color="#6A7092" fontSize="12px" marginTop="6px">
            Defina uma senha definitiva para sua conta.
          </Text>
        </View>
      );
    },
  },
};

const authFormFields = {
  signIn: {
    username: { label: 'E-mail', placeholder: 'seu.email@hydrodataeng.com.br' },
    password: { label: 'Senha', placeholder: '••••••••' },
  },
  forceNewPassword: {
    password: { label: 'Nova senha', placeholder: 'Mínimo 8 caracteres' },
  },
  forgotPassword: {
    username: { label: 'E-mail cadastrado', placeholder: 'seu.email@hydrodataeng.com.br' },
  },
  confirmResetPassword: {
    confirmation_code: { label: 'Código recebido por e-mail', placeholder: '000000' },
    password: { label: 'Nova senha', placeholder: 'Mínimo 8 caracteres' },
    confirm_password: { label: 'Confirme a nova senha', placeholder: 'Repita a senha' },
  },
};

// ─── Definição de módulos e páginas ──────────────────────────
// Refactor (refactor/frontend-react-router): a navegação agora é por URL
// (react-router-dom v6) em vez de setState. Cada aba tem um `path` (URL
// canônica) — usado pela sidebar (NavLink) e pelo array <Routes>.
// O id (legado) é preservado pra:
//   1. compat com useTabsPrefs (ordem custom persistida em localStorage),
//   2. compat com `abasPermitidas` vindo do backend `/meu-perfil`,
//   3. compat com NotificacoesBell.onIrPara(modulo, pagina, id) — agora
//      mapeado via PAGE_ID_TO_PATH.
type Modulo = 'home' | 'financeiro' | 'comercial' | 'operacional' | 'administrativo' | 'juridico' | 'marketing' | 'atividades' | 'admin' | 'nexo';
type Pagina =
  | 'home-dashboard'
  | 'nexo'
  | 'dashboard' | 'profissionais' | 'fin-rateio-clockify' | 'diferimentos' | 'manuais' | 'orcamento' | 'centros-custo' | 'categorias' | 'emprestimos' | 'fin-contas-receber' | 'fin-deliberacao' | 'fin-contas-pagar' | 'fin-extrato-bancario' | 'fin-cartoes' | 'fin-reconcile-notion' | 'fin-fluxo-caixa' | 'fin-medicoes' | 'fin-atividades' | 'fin-reembolsos-campo' | 'fin-combustiveis'
  | 'fin-hub-dre' | 'fin-recebimentos' | 'fin-pagamentos' | 'fin-cadastros'
  | 'com-dashboard' | 'com-metas' | 'com-comissoes' | 'com-indicacoes' | 'com-leads' | 'com-pipeline' | 'com-clientes' | 'com-contatos' | 'com-custos' | 'com-composicoes' | 'com-propostas' | 'com-licitacoes' | 'com-hydra' | 'com-atestados'
  | 'com-painel' | 'com-hub-clientes' | 'com-hub-propostas' | 'com-hub-licitacoes' | 'com-hub-comissoes' | 'com-meu-radar' | 'com-followups'
  | 'op-contratos' | 'op-jim' | 'op-campo' | 'op-diario-campo' | 'op-dados-campo' | 'op-controle-entregas' | 'op-planejamento-campo' | 'op-kits' | 'op-monitoramento' | 'op-reunioes' | 'op-reuniao-geral' | 'op-horas'
  | 'adm-meu-radar' | 'adm-patrimonio' | 'adm-estoque' | 'adm-pam' | 'adm-atividades' | 'patrimonio' | 'adm-tickets' | 'adm-expedientes' | 'adm-documentos' | 'adm-rh' | 'adm-horas' | 'adm-plano-acao'
  | 'jur-contratos'
  | 'ativ-requisicoes' | 'ativ-reembolsos' | 'ativ-ouvidoria'
  | 'admin-usuarios' | 'admin-perfis' | 'admin-permissoes' | 'admin-hierarquia' | 'admin-codigos-convite' | 'admin-dados-tecnicos'
  | 'admin-d4sign' | 'admin-empresa' | 'admin-integracoes' | 'admin-backlog' | 'admin-aws-costs' | 'admin-backup-nas'
  | 'mkt-posts'
  | 'atividades' | 'meu-perfil';

interface AbaConfig {
  id: Pagina;
  label: string;
  modulo: Modulo;
  /** URL canônica da aba (react-router path absoluto, começando com `/`). */
  path: string;
  /** Cognito groups que podem ver esta aba. Vazio = todos. (legado) */
  groups: string[];
  /** Codigo de permissao granular (admin.permissoes_catalogo). Se setado,
   *  tem prioridade sobre `groups` e `abasPermitidas` antigos. Onda B2. */
  perm?: string;
  /** Para itens-hub que agregam várias páginas: visível se o usuário tem
   *  QUALQUER uma destas perms. Usado pelo hub "Campo". */
  perms?: string[];
}

// Conjuntos de grupos (facilita manutenção)
const G_FIN_EDIT = ['admin', 'financeiro', 'financeiro_editor'];   // edita financeiro
const G_FIN_VIEW = ['admin', 'financeiro', 'financeiro_editor', 'financeiro_view', 'diretoria'];  // vê financeiro
const G_COM_EDIT = ['admin', 'comercial'];
const G_COM_VIEW = ['admin', 'comercial'];  // Diretoria temporariamente SEM acesso a Comercial
const G_OP_EDIT  = ['admin', 'comercial', 'operacional'];   // gestores e responsáveis editam contratos
const G_OP_VIEW  = ['admin', 'comercial', 'operacional'];
const G_ADMINISTRATIVO_VIEW = ['admin', 'comercial', 'operacional', 'financeiro', 'financeiro_editor', 'administrativo', 'diretoria'];
const G_ADMINISTRATIVO_EDIT = ['admin', 'financeiro', 'financeiro_editor', 'administrativo'];
// 🚨 Jurídico NÃO é `groups: []`. Contrato de vínculo tem CPF e remuneração
//    dentro; lista vazia retorna true ANTES de olhar `perm` (ver o comentário
//    do adm-documentos). Quem entra é decidido aqui e confirmado no backend.
const G_JURIDICO_VIEW = ['admin', 'diretoria'];
// Hub RH (2026-08-24): a união das audiências das duas abas — quem via
// Profissionais no Financeiro (G_FIN_VIEW) + o pessoal do administrativo, que
// cuida da documentação. 🚨 Nunca `[]`: a lista vazia retorna true ANTES de
// olhar `perm`, e aqui dentro tem salário e documento pessoal.
const G_RH_VIEW = ['admin', 'financeiro', 'financeiro_editor', 'financeiro_view', 'diretoria', 'administrativo'];
const G_MARKETING = ['admin', 'marketing', 'comercial', 'diretoria'];
const G_ADMIN    = ['admin'];

const ABAS: AbaConfig[] = [
  // Módulo Home — visível para qualquer usuário autenticado
  { id: 'home-dashboard', label: 'Visão Geral', modulo: 'home', path: '/', groups: [] },

  // Módulo Financeiro — FIN-HUBS (2026-07-13): 18 itens viraram 7. Cada hub
  // agrega páginas em abas (visível se o usuário tem QUALQUER das perms[]);
  // as rotas antigas seguem vivas em ROTAS_OCULTAS como redirects que
  // preservam a query (deep-links ?parcela= etc. continuam funcionando).
  { id: 'fin-hub-dre',      label: 'DRE & Resultado', modulo: 'financeiro', path: '/financeiro/dre-hub',      groups: G_FIN_VIEW, perms: ['financeiro.dre.ver', 'financeiro.meta.ver', 'financeiro.orcamento.ver', 'financeiro.lancamentos.ver', 'financeiro.rateio_clockify.ver'] },
  { id: 'fin-recebimentos', label: 'Recebimentos',    modulo: 'financeiro', path: '/financeiro/recebimentos', groups: G_FIN_VIEW, perms: ['financeiro.contas_receber.ver', 'financeiro.medicoes_bm.ver'] },
  { id: 'fin-pagamentos',   label: 'Pagamentos',      modulo: 'financeiro', path: '/financeiro/pagamentos',   groups: G_FIN_VIEW, perms: ['financeiro.contas_pagar.ver', 'financeiro.emprestimos.ver', 'financeiro.diferimentos.ver'] },
  { id: 'fin-fluxo-caixa',  label: 'Fluxo de Caixa',  modulo: 'financeiro', path: '/financeiro/fluxo-caixa',  groups: G_FIN_VIEW, perm: 'financeiro.fluxo_caixa.ver' },
  // F5: fin-extrato-bancario reusa perm de contas_pagar (mesmo escopo funcional).
  { id: 'fin-extrato-bancario', label: 'Extrato bancário (OFX)', modulo: 'financeiro', path: '/financeiro/extrato-bancario', groups: G_FIN_EDIT, perm: 'financeiro.contas_pagar.ver' },
  // 24/08: Profissionais saiu daqui para o hub RH — a perm dele saiu do
  // perms[] junto (senão quem só a tem veria um hub vazio). E entrou a
  // financeiro.contas.ver, que sempre faltou: a aba Contas & Saldos existia,
  // mas quem só tinha essa perm não via o item no menu.
  { id: 'fin-cadastros',    label: 'Cadastros',       modulo: 'financeiro', path: '/financeiro/cadastros',    groups: G_FIN_VIEW, perms: ['financeiro.contas.ver', 'financeiro.categorias.ver', 'financeiro.centros_custo.ver', 'financeiro.avisos.ver', 'financeiro.fornecedores.ver'] },
  { id: 'fin-atividades',   label: 'Atividades',         modulo: 'financeiro', path: '/financeiro/atividades',   groups: G_FIN_VIEW, perm: 'financeiro.atividades.ver' },

  // Módulo Comercial — COM-HUBS (2026-08-21): 13 itens viraram 6; o funil
  // de Leads acrescentado em 2026-09-04 ficou como a 7a entrada. Mesma
  // mecânica do FIN-HUBS: cada hub agrega páginas em abas (visível se o
  // usuário tem QUALQUER das perms[]) e cada hub REUSA o path da página
  // principal que engoliu, então os links antigos já caem no lugar certo.
  // Pipeline ficou de fora de propósito (decisão Lucas 2026-08-21): é a tela
  // do dia a dia do comercial e continua a um clique. Leads segue a mesma
  // logica porque e o trabalho anterior ao Pipeline.
  { id: 'com-painel',        label: 'Painel',              modulo: 'comercial', path: '/comercial',             groups: G_COM_VIEW, perms: ['comercial.dashboard.ver', 'comercial.metas.ver'] },
  { id: 'com-leads',         label: 'Leads',               modulo: 'comercial', path: '/comercial/leads',       groups: G_COM_VIEW, perm: 'comercial.leads.ver' },
  { id: 'com-pipeline',      label: 'Pipeline',            modulo: 'comercial', path: '/comercial/pipeline',    groups: G_COM_VIEW, perm: 'comercial.pipeline.ver' },
  // Follow-ups do pipeline privado (05/10/2026): perms = união das abas do hub.
  { id: 'com-followups',     label: 'Follow-ups',          modulo: 'comercial', path: '/comercial/followups',   groups: G_COM_VIEW, perms: ['comercial.pipeline.ver', 'comercial.followups.configurar'] },
  // MEU RADAR (24/09/2026): saiu do Administrativo e virou hub do Comercial,
  // com a aba Atividades ao lado (pedido do Gustavo). O perms[] é a UNIÃO das
  // perms das duas abas (docs/menu-hubs.md).
  // 🚨 NUNCA `groups: []` aqui: userCanSee libera lista vazia ANTES de olhar
  // perms/perm. O groups não vazio impede que perfis sem nenhuma das perms do
  // item (ex.: topógrafo, sócio) vejam o Radar — com groups [] eles viam, que
  // era o que o antigo `adm-meu-radar` fazia de propósito. A audiência efetiva
  // é quem tem radar.meu.ver (hoje os 8 perfis de atividades.minhas.ver, até o
  // PR 2 restringir por migration) ou comercial.pipeline.ver. O id antigo
  // segue em ROTAS_OCULTAS (sinos já gravados).
  { id: 'com-meu-radar',     label: 'Meu Radar',           modulo: 'comercial', path: '/comercial/radar',       groups: G_COM_VIEW, perms: ['radar.meu.ver', 'comercial.pipeline.ver'] },
  { id: 'com-hub-licitacoes',label: 'Licitações',          modulo: 'comercial', path: '/comercial/licitacoes',  groups: G_COM_EDIT, perms: ['comercial.licitacoes.pipeline.ver', 'comercial.licitacoes.oportunidades.ver', 'comercial.atestados.ver', 'comercial.licitacoes.documentos_empresa.ver', 'admin.integracoes.gerenciar'] },
  // `comercial.portais.ver` entra no perms[] junto com a aba (2026-09-03):
  // quem receber SO' essa permissao precisa enxergar o item Clientes no menu,
  // senao a aba existe e nao ha caminho ate' ela.
  { id: 'com-hub-clientes',  label: 'Clientes',            modulo: 'comercial', path: '/comercial/clientes',    groups: G_COM_EDIT, perms: ['comercial.empresas.ver', 'comercial.contatos.ver', 'comercial.portais.ver'] },
  { id: 'com-hub-propostas', label: 'Propostas & Preços',  modulo: 'comercial', path: '/comercial/propostas',   groups: G_COM_EDIT, perms: ['comercial.documentos.propostas.ver', 'comercial.composicoes.ver', 'comercial.custos_unitarios.ver'] },
  // ⚠️ REMVAR: Comissões e Indicações são de TODO MUNDO (cada pessoa vê a
  // própria comissão; qualquer um registra que indicou, sem saber usar CRM).
  // Por isso este hub tem grupos AMPLOS — não pode ser fundido nos de cima.
  { id: 'com-hub-comissoes', label: 'Comissões & Indicações', modulo: 'comercial', path: '/comercial/comissoes', groups: G_ADMINISTRATIVO_VIEW, perms: ['comercial.comissoes.ver', 'comercial.indicacoes.criar'] },
  { id: 'com-hydra',         label: 'Dwight',               modulo: 'comercial', path: '/comercial/hydra',       groups: G_COM_EDIT, perm: 'comercial.dwight.configuracao.ver' },

  // Módulo Operacional (Onda Op-1)
  { id: 'op-contratos',  label: 'Contratos',    modulo: 'operacional', path: '/operacional/contratos',  groups: G_OP_VIEW, perm: 'operacional.projetos.listar' },
  // Mapa Geral (2026-08-21): virou vista no topo de Contratos — é outro jeito de
  // olhar a mesma carteira, não um módulo à parte. Path antigo redireciona.
  { id: 'op-jim',        label: 'Jim',        modulo: 'operacional', path: '/operacional/jim',        groups: G_OP_EDIT, perm: 'operacional.painel_geral.jim.usar' },
  // Campo (2026-06-19): hub único com abas (Diário | Dados | Planejamento | Kits).
  // Visível se o usuário vê QUALQUER uma das 4 (perms[]). As páginas seguem
  // acessíveis por URL direta via ROTAS_OCULTAS.
  { id: 'op-campo', label: 'Campo', modulo: 'operacional', path: '/operacional/campo', groups: G_OP_VIEW, perms: ['operacional.diario_campo.listar', 'operacional.dados_campo.listar', 'operacional.planejamento_campo.ver', 'operacional.kits.ver'] },
  // 'Controle de Entregas' (2026-06-19): saiu do menu — virou alternador no
  // topo da página Contratos. Rota segue em ROTAS_OCULTAS (deep-links).
  // WEEKLY (2026-07-27): reuniões semanais — briefing seg 8h + ata + encaminhamentos
  { id: 'op-reunioes', label: 'Reuniões', modulo: 'operacional', path: '/operacional/reunioes', groups: G_OP_VIEW, perm: 'operacional.reunioes.ver' },
  // REUNIÃO GERAL (2026-09-03): a reunião de sexta 14h com TODO o escritório.
  // `groups: []` + perm concedida a todos os perfis — mesmo padrão de
  // Expedientes. Item PRÓPRIO, e não aba de Reuniões, de propósito: a doutrina
  // do hub é "audiência = a mais larga das abas", e virar hub obrigaria
  // `groups: []` no item da Weekly, que é da gerência. O gate real é o backend,
  // que só devolve pauta PUBLICADA para quem não tem .gerenciar.
  { id: 'op-reuniao-geral', label: 'Reunião Geral', modulo: 'operacional', path: '/operacional/reuniao-geral', groups: [], perm: 'operacional.reuniao_geral.ver' },
  // HORAS (2026-08-21): apontamento próprio, substituindo o Clockify. Todo mundo
  // que executa tem `horas.apontar` — por isso o item fica visível para quem
  // trabalha, não só para a gerência.
  { id: 'op-horas', label: 'Horas', modulo: 'operacional', path: '/operacional/horas', groups: G_OP_VIEW, perm: 'horas.apontar' },
  // Monitoramento (2026-08-21): hub único com abas (Estações | Portal do
  // Cliente). Eram dois itens vizinhos na sidebar tratando do mesmo assunto —
  // o portal existe por causa das estações. Visível pra quem vê QUALQUER uma
  // das duas (perms[]); os paths antigos viram RedirectHub com a aba certa.
  { id: 'op-monitoramento', label: 'Monitoramento Hidrológico', modulo: 'operacional', path: '/operacional/monitoramento', groups: G_OP_VIEW, perms: ['monitoramento.estacoes.ver', 'monitoramento.portal.gerenciar'] },

  // Módulo Administrativo (Sprint A.4) — patrimônio + estoque + Pam
  { id: 'adm-patrimonio',          label: 'Patrimônio',             modulo: 'administrativo', path: '/administrativo/patrimonio',           groups: G_ADMINISTRATIVO_VIEW, perm: 'administrativo.patrimonio.ver' },
  { id: 'adm-estoque',             label: 'Estoque',                modulo: 'administrativo', path: '/administrativo/estoque',              groups: G_ADMINISTRATIVO_VIEW, perm: 'administrativo.estoque.inventario.ver' },
  // Meu Radar: saiu daqui em 24/09/2026 e virou o hub `com-meu-radar`
  // (Comercial › Meu Radar). O id `adm-meu-radar` segue em ROTAS_OCULTAS.
  { id: 'adm-atividades',          label: 'Atividades',             modulo: 'administrativo', path: '/administrativo/atividades',           groups: G_ADMINISTRATIVO_VIEW, perm: 'administrativo.atividades.ver' },
  // ADM-1 (2026-06-12): central dos tickets abertos pela equipe (requisições + ouvidoria)
  { id: 'adm-tickets',             label: 'Central de Tickets',  modulo: 'administrativo', path: '/administrativo/tickets',              groups: G_ADMINISTRATIVO_VIEW, perm: 'administrativo.tickets.gerenciar' },
  // EXPEDIENTES (2026-08-13): acervo numerado de ofícios/declarações/memorandos.
  // groups vazio + perm concedida a TODOS os perfis: qualquer um emite ofício,
  // e o sigilo é por documento (envolvidos), não por módulo.
  { id: 'adm-expedientes',         label: 'Expedientes',         modulo: 'administrativo', path: '/administrativo/expedientes',          groups: [], perm: 'administrativo.expedientes.ver' },
  // DOCUMENTOS DA EMPRESA (2026-08-19): o acervo saiu de Comercial › Licitações.
  // ⚠️ groups NÃO pode ser [] aqui: userCanSee devolve true para lista vazia
  // ANTES de olhar `perm` (é assim que Expedientes fica aberto a todos, lá de
  // propósito). Documento societário não pode aparecer para topógrafo.
  { id: 'adm-documentos',          label: 'Documentos da empresa', modulo: 'administrativo', path: '/administrativo/documentos',        groups: G_ADMINISTRATIVO_VIEW, perm: 'administrativo.documentos_empresa.ver' },
  // HUB RH (2026-08-24): Profissionais (veio do Financeiro › Cadastros, mesma
  // perm — que também gateia o salário na API) + Documentos por pessoa
  // (contrato, ASO, EPI…; perm nova da categoria restrita `rh`).
  { id: 'adm-rh',                  label: 'RH',                   modulo: 'administrativo', path: '/administrativo/rh',                   groups: G_RH_VIEW, perms: ['financeiro.profissionais.ver', 'administrativo.rh.documentos.ver'] },
  // HORAS DA EQUIPE (2026-10-01): o painel de quem anotou, quanto falta e para
  // onde foram as horas. Substitui o placar "Equipe" de Operacional › Horas.
  // `perm` manda (o servidor confere de novo); groups só vale se a matriz de
  // permissões não carregar — e aí só o admin vê. 🚨 Nunca `[]` (libera a todos).
  { id: 'adm-horas',               label: 'Horas da equipe',      modulo: 'administrativo', path: '/administrativo/horas',                groups: G_ADMIN, perm: 'horas.ver_equipe' },
  // PLANO DE AÇÃO (06/10/2026): ciclo de trabalho com a consultoria (semanas, tarefas, decisões,
  // indicadores). `perm` manda (os 4 do plano têm o perfil); groups só vale se a matriz não carregar e fica G_ADMIN
  // de propósito, como adm-horas: grupo largo abriria o item a todo o comercial/diretoria/operacional. 🚨 Nunca [] aqui (docs/menu-hubs.md).
  { id: 'adm-plano-acao',          label: 'Plano de ação',        modulo: 'administrativo', path: '/administrativo/plano-acao',           groups: G_ADMIN, perm: 'gestao.plano_acao.ver' },
  // F5: adm-pam reusa perm de estoque/materiais (Pam é o agente do almoxarife).
  { id: 'adm-pam',                 label: 'Pam',                  modulo: 'administrativo', path: '/administrativo/pam',                  groups: G_ADMINISTRATIVO_EDIT, perm: 'administrativo.estoque.inventario.ver' },

  // Módulo Jurídico (2026-08-20) — contratos redigidos e de terceiros
  { id: 'jur-contratos', label: 'Contratos', modulo: 'juridico', path: '/juridico/contratos', groups: G_JURIDICO_VIEW, perm: 'juridico.contratos.ver' },

  // Módulo Marketing (Sprint M.1) — gestão de posts pra redes sociais
  { id: 'mkt-posts', label: 'Posts', modulo: 'marketing', path: '/marketing/posts', groups: G_MARKETING, perm: 'marketing.posts.ver' },

  // Módulo Atividades (cross-módulo, todo usuário autenticado vê suas)
  { id: 'atividades', label: 'Minhas Atividades', modulo: 'atividades', path: '/atividades', groups: [], perm: 'atividades.minhas.ver' },
  // ADM-1 (2026-06-12): sub-módulos abertos a toda a equipe — pedidos pro
  // administrativo, reembolso (igual PWA) e ouvidoria anônima
  { id: 'ativ-requisicoes', label: 'Requisições', modulo: 'atividades', path: '/atividades/requisicoes', groups: [], perm: 'atividades.requisicoes.criar' },
  { id: 'ativ-reembolsos',  label: 'Reembolsos',  modulo: 'atividades', path: '/atividades/reembolsos',  groups: [], perm: 'pwa.reembolso.criar' },
  { id: 'ativ-ouvidoria',   label: 'Ouvidoria',   modulo: 'atividades', path: '/atividades/ouvidoria',   groups: [], perm: 'atividades.ouvidoria.criar' },

  { id: 'nexo', label: 'Capacidades e evolução', modulo: 'nexo', path: '/nexo', groups: G_ADMIN },
  // Módulo Admin — cada item tem sua perm específica (fix 2026-05-28).
  // Antes (F5) todos usavam admin.permissoes.editar uniforme, o que impedia
  // dar acesso granular (ex: gerente_financeiro ver Custos AWS sem ter
  // acesso à matriz de Permissões). Cada perm já existe no catálogo.
  // E4: admin-usuarios e admin-perfis legacy ficam só em ROTAS_OCULTAS.
  { id: 'admin-permissoes',  label: 'Permissões',      modulo: 'admin', path: '/admin/permissoes',  groups: G_ADMIN, perm: 'admin.permissoes.editar' },
  { id: 'admin-dados-tecnicos', label: 'Dados Técnicos (CREA/RNP)', modulo: 'admin', path: '/admin/dados-tecnicos', groups: G_ADMIN, perm: 'admin.usuario_dados_tecnicos.ver' },
  { id: 'admin-codigos-convite', label: 'Códigos de Convite (Campo)', modulo: 'admin', path: '/admin/codigos-convite', groups: G_ADMIN, perm: 'admin.usuarios.gerenciar' },
  { id: 'admin-integracoes', label: 'Integrações',        modulo: 'admin', path: '/admin/integracoes', groups: G_ADMIN, perm: 'admin.integracoes.gerenciar' },
  { id: 'admin-empresa',     label: 'Empresa',            modulo: 'admin', path: '/admin/empresa',     groups: G_ADMIN, perm: 'admin.empresa.editar' },
  { id: 'admin-backlog',     label: 'Backlog do sistema', modulo: 'admin', path: '/admin/backlog',     groups: G_ADMIN, perm: 'admin.backlog.gerenciar' },
  { id: 'admin-aws-costs',   label: 'Custos AWS',      modulo: 'admin', path: '/admin/custos-aws',  groups: G_ADMIN, perm: 'admin.aws_custos.ver' },
  { id: 'admin-backup-nas',  label: 'Backup NAS',      modulo: 'admin', path: '/admin/backup-nas',  groups: G_ADMIN, perm: 'admin.aws_custos.ver' },
];

// Rotas extras (não aparecem na sidebar, mas precisam existir):
// - patrimonio: compat URL antiga (alias de adm-patrimonio)
// - fin-reconcile-notion: diagnóstico Notion (movido pra Admin > Integrações)
// - admin-d4sign: compat (movido pra Admin > Integrações > aba D4Sign)
// - admin-hierarquia: acessado via Atividades > Gerenciar equipe
// - meu-perfil: acessado via dropdown do avatar no header
const ROTAS_OCULTAS: { id: Pagina; path: string; groups: string[] }[] = [
  { id: 'patrimonio',          path: '/patrimonio',                 groups: G_ADMINISTRATIVO_VIEW },
  { id: 'fin-reconcile-notion',path: '/financeiro/reconcile-notion',groups: G_FIN_EDIT },
  { id: 'admin-d4sign',        path: '/admin/d4sign',               groups: G_ADMIN },
  { id: 'admin-hierarquia',    path: '/admin/hierarquia',           groups: G_ADMIN },
  // E4 (2026-05-28): admin-usuarios e admin-perfis legacy — escondidos da sidebar,
  // mantidos por URL direta enquanto a migração completa pra Permissões termina.
  { id: 'admin-usuarios',      path: '/admin/usuarios',             groups: G_ADMIN },
  { id: 'admin-perfis',        path: '/admin/perfis',               groups: G_ADMIN },
  { id: 'meu-perfil',          path: '/perfil',                     groups: [] },
  // Campo (2026-06-19): as 4 viraram abas em /operacional/campo, mas seguem
  // acessíveis por URL direta (deep-links, notificações, links antigos).
  { id: 'op-diario-campo',       path: '/operacional/diario-campo',       groups: G_OP_VIEW },
  { id: 'op-dados-campo',        path: '/operacional/dados-campo',        groups: G_OP_VIEW },
  { id: 'op-planejamento-campo', path: '/operacional/planejamento-campo', groups: G_OP_VIEW },
  { id: 'op-kits',               path: '/operacional/kits',               groups: G_OP_VIEW },
  // Controle de Entregas (2026-06-19): virou alternador dentro de Contratos;
  // rota mantida por URL direta (deep-links, notificações).
  { id: 'op-controle-entregas',  path: '/operacional/controle-entregas',  groups: G_OP_VIEW },
  // FIN-HUBS (2026-07-13): as 15 páginas financeiras agrupadas em hubs — os
  // paths antigos viram redirects (RedirectHub) que preservam a query string,
  // então deep-links/notificações/links salvos continuam funcionando.
  { id: 'dashboard',            path: '/financeiro/dre',              groups: G_FIN_VIEW },
  { id: 'orcamento',            path: '/financeiro/orcamento',        groups: G_FIN_VIEW },
  { id: 'manuais',              path: '/financeiro/lancamentos',      groups: G_FIN_EDIT },
  { id: 'fin-rateio-clockify',  path: '/financeiro/rateio-clockify',  groups: G_FIN_EDIT },
  { id: 'fin-contas-receber',   path: '/financeiro/contas-receber',   groups: G_FIN_VIEW },
  { id: 'fin-medicoes',         path: '/financeiro/medicoes',         groups: G_FIN_EDIT },
  { id: 'fin-deliberacao',      path: '/financeiro/deliberacao',      groups: G_ADMIN },
  { id: 'fin-contas-pagar',     path: '/financeiro/contas-pagar',     groups: G_FIN_VIEW },
  { id: 'fin-cartoes',          path: '/financeiro/cartoes',          groups: G_FIN_EDIT },
  { id: 'fin-reembolsos-campo', path: '/financeiro/reembolsos-campo', groups: G_FIN_EDIT },
  // Controle de combustíveis (Ticket Log, 02/10/2026): aba do hub Pagamentos;
  // o id existe para o sino da nota a conferir achar o caminho.
  { id: 'fin-combustiveis',     path: '/financeiro/combustiveis',     groups: G_FIN_EDIT },
  { id: 'emprestimos',          path: '/financeiro/emprestimos',      groups: G_FIN_EDIT },
  { id: 'diferimentos',         path: '/financeiro/diferimentos',     groups: G_FIN_EDIT },
  { id: 'categorias',           path: '/financeiro/categorias',       groups: G_FIN_EDIT },
  { id: 'centros-custo',        path: '/financeiro/centros-custo',    groups: G_FIN_VIEW },
  { id: 'profissionais',        path: '/financeiro/profissionais',    groups: G_FIN_EDIT },
  // COM-HUBS (2026-08-21): ids antigos do Comercial seguem resolvendo pra
  // uma URL (sino, atalhos da Visão Geral, `app-navigate`, links salvos).
  // Quem virou a PRIMEIRA aba do hub aponta pro path do hub (que é o mesmo de
  // antes); o resto aponta pro path antigo, que virou redirect com ?aba=.
  { id: 'com-dashboard',    path: '/comercial',              groups: G_COM_VIEW },
  { id: 'com-metas',        path: '/comercial/metas',        groups: G_COM_VIEW },
  { id: 'com-comissoes',    path: '/comercial/comissoes',    groups: G_ADMINISTRATIVO_VIEW },
  { id: 'com-indicacoes',   path: '/comercial/indicacoes',   groups: G_ADMINISTRATIVO_VIEW },
  { id: 'com-clientes',     path: '/comercial/clientes',     groups: G_COM_EDIT },
  { id: 'com-contatos',     path: '/comercial/contatos',     groups: G_COM_EDIT },
  { id: 'com-custos',       path: '/comercial/custos',       groups: G_COM_EDIT },
  { id: 'com-composicoes',  path: '/comercial/composicoes',  groups: G_COM_EDIT },
  { id: 'com-propostas',    path: '/comercial/propostas',    groups: G_COM_EDIT },
  { id: 'com-licitacoes',   path: '/comercial/licitacoes',   groups: G_COM_EDIT },
  { id: 'com-atestados',    path: '/comercial/atestados',    groups: G_COM_EDIT },
  // MEU RADAR (24/09/2026): o item saiu do Administrativo para o Comercial.
  // Sinos de lembrete já gravados têm link_pagina 'adm-meu-radar'; sem esta
  // linha, PAGE_ID_TO_PATH fica sem entrada e a notificação não navega.
  { id: 'adm-meu-radar',    path: '/comercial/radar',        groups: G_COM_VIEW },
];

// Tabela id → path (consulta rápida pra navegação programática via custom event)
const PAGE_ID_TO_PATH: Record<string, string> = (() => {
  const m: Record<string, string> = {};
  for (const a of ABAS) m[a.id] = a.path;
  for (const r of ROTAS_OCULTAS) m[r.id] = r.path;
  return m;
})();

const MODULOS: { id: Modulo; label: string; icon: string }[] = [
  { id: 'home',           label: 'Visão Geral',     icon: '🏠' },
  { id: 'financeiro',     label: 'Financeiro',      icon: '📊' },
  { id: 'comercial',      label: 'Comercial',       icon: '🤝' },
  { id: 'operacional',    label: 'Operacional',     icon: '⚙️' },
  { id: 'administrativo', label: 'Administrativo',  icon: '📦' },
  { id: 'juridico',       label: 'Jurídico',        icon: '⚖️' },
  { id: 'marketing',      label: 'Marketing',       icon: '📱' },
  // ADM-1 (2026-06-12): renomeado de "Atividades" — o módulo agora reúne
  // Minhas Atividades + Requisições + Reembolsos + Ouvidoria. O id interno
  // continua 'atividades' (rotas e links do sino não mudam).
  { id: 'atividades',     label: 'Central do Usuário', icon: '✓' },
  { id: 'admin',          label: 'Admin',           icon: '🔒' },
  { id: 'nexo',           label: 'NEXO',            icon: '🧩' },
];

// Ícones (Tabler) usados no sidebar Mantine. Substituem os emojis do MODULOS
// no chrome novo, mantidos no objeto acima caso algum lugar ainda os referencie.
const ICONE_DO_MODULO: Record<string, any> = {
  home:           <IconHome size={18} stroke={1.6} />,
  financeiro:     <IconChartBar size={18} stroke={1.6} />,
  comercial:      <IconBriefcase size={18} stroke={1.6} />,
  operacional:    <IconSettings size={18} stroke={1.6} />,
  administrativo: <IconBox size={18} stroke={1.6} />,
  // A balança espelha o ⚖️ que o módulo já usa no MODULOS e na aba Contratos.
  // Sem esta linha o Jurídico era o único item do menu sem ícone: o
  // `leftSection` recebia undefined e o rótulo ficava desalinhado dos outros.
  juridico:       <IconScale size={18} stroke={1.6} />,
  marketing:      <IconBrandInstagram size={18} stroke={1.6} />,
  atividades:     <IconCircleCheck size={18} stroke={1.6} />,
  admin:          <IconLock size={18} stroke={1.6} />,
  nexo:           <IconSettings size={18} stroke={1.6} />,
};

// Ícone de cada PÁGINA do menu (2026-08-21).
//
// Antes o menu tinha três estilos ao mesmo tempo: página com emoji colorido
// dentro do rótulo ("📄 Expedientes"), página sem ícone nenhum ("Patrimônio")
// e módulo com ícone de traço. A lista não alinhava, e a cor do emoji competia
// com o destaque do item selecionado.
//
// Um mapa por `id`, e não um campo em cada linha do ABAS: o array continua
// sendo DADO (id, rótulo, rota, permissão) e o desenho fica junto do desenho.
// Página sem entrada aqui simplesmente não ganha ícone — não quebra nada.
const ICONE_DA_PAGINA: Record<string, any> = {
  'home-dashboard':        <IconHome size={16} stroke={1.5} />,
  // Financeiro
  'fin-hub-dre':           <IconChartPie size={16} stroke={1.5} />,
  'fin-recebimentos':      <IconCash size={16} stroke={1.5} />,
  'fin-pagamentos':        <IconReceipt size={16} stroke={1.5} />,
  'fin-fluxo-caixa':       <IconTrendingUp size={16} stroke={1.5} />,
  'fin-extrato-bancario':  <IconBuildingBank size={16} stroke={1.5} />,
  'fin-cadastros':         <IconAdjustments size={16} stroke={1.5} />,
  'fin-atividades':        <IconChecklist size={16} stroke={1.5} />,
  // Comercial (virou hubs em 2026-08; os ids antigos por página saíram)
  'com-painel':            <IconChartBar size={16} stroke={1.5} />,
  'com-leads':             <IconUserSearch size={16} stroke={1.5} />,
  'com-pipeline':          <IconChartFunnel size={16} stroke={1.5} />,
  'com-followups':         <IconMailForward size={16} stroke={1.5} />,
  'com-hub-licitacoes':    <IconGavel size={16} stroke={1.5} />,
  'com-hub-clientes':      <IconBuilding size={16} stroke={1.5} />,
  'com-hub-propostas':     <IconFileDescription size={16} stroke={1.5} />,
  'com-hub-comissoes':     <IconPercentage size={16} stroke={1.5} />,
  'com-meu-radar':         <IconRadar size={16} stroke={1.5} />,
  'com-hydra':             <IconRobot size={16} stroke={1.5} />,
  // Operacional
  'op-contratos':          <IconFileText size={16} stroke={1.5} />,
  'op-monitoramento':      <IconRadar size={16} stroke={1.5} />,
  'op-jim':                <IconRobot size={16} stroke={1.5} />,
  'op-campo':              <IconTent size={16} stroke={1.5} />,
  'op-reunioes':           <IconCalendarEvent size={16} stroke={1.5} />,
  'op-reuniao-geral':      <IconUsersGroup size={16} stroke={1.5} />,
  'op-horas':              <IconClock size={16} stroke={1.5} />,
  // Administrativo
  'adm-patrimonio':        <IconBuildingWarehouse size={16} stroke={1.5} />,
  'adm-estoque':           <IconPackages size={16} stroke={1.5} />,
  'adm-atividades':        <IconChecklist size={16} stroke={1.5} />,
  'adm-tickets':           <IconTicket size={16} stroke={1.5} />,
  'adm-expedientes':       <IconFileDescription size={16} stroke={1.5} />,
  'adm-documentos':        <IconFolders size={16} stroke={1.5} />,
  'adm-rh':                <IconIdBadge2 size={16} stroke={1.5} />,
  'adm-horas':             <IconClockCheck size={16} stroke={1.5} />,
  'adm-plano-acao':        <IconTargetArrow size={16} stroke={1.5} />,
  'adm-pam':               <IconRobot size={16} stroke={1.5} />,
  // Jurídico
  'jur-contratos':         <IconFileText size={16} stroke={1.5} />,
  // Marketing
  'mkt-posts':             <IconNews size={16} stroke={1.5} />,
  // Central do usuário
  'atividades':            <IconCircleCheck size={16} stroke={1.5} />,
  'ativ-requisicoes':      <IconInbox size={16} stroke={1.5} />,
  'ativ-reembolsos':       <IconReceipt size={16} stroke={1.5} />,
  'ativ-ouvidoria':        <IconSpeakerphone size={16} stroke={1.5} />,
  // Admin
  'admin-permissoes':      <IconShieldLock size={16} stroke={1.5} />,
  'admin-dados-tecnicos':  <IconSchool size={16} stroke={1.5} />,
  'admin-codigos-convite': <IconKey size={16} stroke={1.5} />,
  'admin-integracoes':     <IconPlugConnected size={16} stroke={1.5} />,
  'admin-empresa':         <IconBuilding size={16} stroke={1.5} />,
  'admin-backlog':         <IconListCheck size={16} stroke={1.5} />,
  'admin-aws-costs':       <IconCloud size={16} stroke={1.5} />,
  'admin-backup-nas':      <IconDatabase size={16} stroke={1.5} />,
};

// ─── Helpers ──────────────────────────────────────────────────
function userCanSee(
  aba: AbaConfig | { id: Pagina; groups: string[]; perm?: string; perms?: string[] },
  groups: string[],
  abasPermitidas: string[] | null,
  pode?: (codigo: string) => boolean,
  // Incidente 2026-09-01: quando a matriz NÃO carrega (Lambda fora do ar), `pode`
  // devolve false para tudo e o menu some inteiro para quem não é admin. Sem a
  // matriz não se sabe o que o usuário pode — então voltamos ao critério antigo
  // (abasPermitidas/grupos). Mostrar a aba não concede nada: o backend continua
  // barrando por checarPerm; esconder tudo, sim, tira o sistema de quem tem acesso.
  permsIndisponivel?: boolean,
): boolean {
  // Admin sempre vê tudo (fail-safe caso tabela de permissões fique vazia).
  if (groups.includes('admin')) return true;
  // Páginas públicas (sem groups requeridos) sempre passam — antes esse
  // check vinha DEPOIS de abasPermitidas, o que escondia páginas tipo
  // Visão Geral (home-dashboard) pra perfis com matriz antiga não-vazia.
  if (aba.groups && aba.groups.length === 0) return true;
  // Item-hub que agrega páginas (ex.: Campo): visível se o usuário vê QUALQUER
  // uma das perms declaradas.
  if (aba.perms && aba.perms.length && pode && !permsIndisponivel) return aba.perms.some((p) => pode(p));
  // Onda B2: matriz granular (admin.perfis + permissoes_catalogo).
  // Se aba declarou `perm`, esse é a fonte de verdade — desde que ela tenha carregado.
  if (aba.perm && pode && !permsIndisponivel) return pode(aba.perm);
  // Fonte dinâmica antiga (backend /meu-perfil retorna `abasPermitidas`).
  if (abasPermitidas) return abasPermitidas.includes(aba.id);
  // Fallback hardcoded caso /meu-perfil falhe.
  return aba.groups.some(g => groups.includes(g));
}

function userCanSeeModulo(
  modulo: Modulo,
  groups: string[],
  abasPermitidas: string[] | null,
  abas: AbaConfig[],
  pode?: (codigo: string) => boolean,
  permsIndisponivel?: boolean,
): boolean {
  // Home é sempre visível para qualquer usuário autenticado.
  if (modulo === 'home') return true;
  return abas.filter(a => a.modulo === modulo)
    .some(a => userCanSee(a, groups, abasPermitidas, pode, permsIndisponivel));
}

// Dada uma URL atual, devolve a aba (ABA ou rota oculta) que corresponde.
// Match prioriza paths mais específicos (mais longos) pra evitar `/comercial`
// engolir `/comercial/pipeline`.
function abaPorPath(pathname: string): AbaConfig | undefined {
  const todas = [...ABAS].sort((a, b) => b.path.length - a.path.length);
  return todas.find(a => matchPath({ path: a.path, end: true }, pathname));
}

// ─── ProtectedRoute: redireciona pra / se user não tem permissão ─────
// Decisão: validamos pelo `id` da aba (não pelo path), já que o backend
// retorna ids. `groupsRequiridos` é fallback quando não há resposta do
// /meu-perfil ainda (ex: rota oculta como `meu-perfil`).
interface ProtectedRouteProps {
  pageId: Pagina;
  groupsFallback: string[];
  userGroups: string[];
  abasPermitidas: string[] | null;
  children: ReactNode;
}
function ProtectedRoute({ pageId, groupsFallback, userGroups, abasPermitidas, children }: ProtectedRouteProps) {
  const { pode, erro: permsErro, carregando: permsCarregando, codigos } = usePerms();
  // Resolve a aba pra pegar tambem o `perm` granular (alem de groups fallback)
  const aba = ABAS.find(a => a.id === pageId);
  const abaCheck = aba
    ? { id: pageId, groups: aba.groups, perm: aba.perm, perms: aba.perms }
    : { id: pageId, groups: groupsFallback };
  const canSee = userCanSee(abaCheck as any, userGroups, abasPermitidas, pode, permsErro);
  if (!canSee) {
    // 🚨 "Não pode" só vale depois que a matriz chegou (24/09/2026). Os grupos
    // saem do token (local, rápido) e liberam as rotas ANTES de a matriz de
    // permissões voltar da rede; nesse meio-tempo `pode()` responde false para
    // tudo. Sem esta espera, quem não é admin e abria qualquer link direto
    // numa aba nova (o "Abrir no sistema" do e-mail de licitação, um F5) era
    // mandado para "/" com replace — a URL com `?oportunidade=` se perdia e a
    // pessoa ficava na Visão Geral mesmo depois de as permissões chegarem.
    // Só espera a PRIMEIRA carga: com a matriz em mãos (ou com `erro`, que cai
    // no critério antigo de grupos), a decisão é a de sempre.
    if (permsCarregando && codigos.size === 0) {
      return <div style={{ padding: 24, color: HD_BLUE, fontSize: 13 }}>Carregando permissões...</div>;
    }
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

// FIN-HUBS (2026-07-13): redirect de rota antiga → hub, PRESERVANDO a query
// string (um <Navigate to="..."> estático perderia o ?parcela= dos deep-links).
// `param` existe porque nem todo destino usa `?aba=`: Contratos alterna as
// vistas por `?vista=` (Lista | Controle de Entregas | Mapa Geral), num
// Segmented, não em Tabs.
function RedirectHub({ hub, aba, param = 'aba' }: { hub: string; aba: string; param?: string }) {
  const location = useLocation();
  const sp = new URLSearchParams(location.search);
  sp.set(param, aba);
  return <Navigate to={`${hub}?${sp.toString()}`} replace />;
}

// ─── Wrappers para páginas públicas (extraem :hash do useParams) ─────
function PublicoPropostaRoute() {
  const { hash } = useParams<{ hash: string }>();
  if (!hash || !/^[a-zA-Z0-9_-]+$/.test(hash)) return <LinkInvalido />;
  return <PaginaPublicaProposta hash={hash} />;
}
function PublicoClienteRoute() {
  const { hash } = useParams<{ hash: string }>();
  if (!hash || !/^[a-zA-Z0-9_-]+$/.test(hash)) return <LinkInvalido />;
  return <PaginaPublicaCliente hash={hash} />;
}
function PublicoBmRoute() {
  const { hash } = useParams<{ hash: string }>();
  if (!hash || !/^[a-zA-Z0-9_-]+$/.test(hash)) return <LinkInvalido />;
  return <PaginaPublicaBm hash={hash} />;
}
function PublicoDriveRoute() {
  const { hash } = useParams<{ hash: string }>();
  if (!hash || !/^[a-zA-Z0-9_-]+$/.test(hash)) return <LinkInvalido />;
  return <PaginaPublicaDrive hash={hash} />;
}
function PublicoPlanejamentoRoute() {
  const { hash } = useParams<{ hash: string }>();
  if (!hash || !/^[a-zA-Z0-9_-]+$/.test(hash)) return <LinkInvalido />;
  return <PaginaPublicaPlanejamento hash={hash} />;
}
function PublicoEstacaoRoute() {
  const { token } = useParams<{ token: string }>();
  if (!token || !/^[a-zA-Z0-9_-]+$/.test(token)) return <LinkInvalido />;
  return <PaginaPublicaEstacao token={token} />;
}
function LinkInvalido() {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center',
      justifyContent: 'center', background: '#F0F1F5', padding: 20 }}>
      <div style={{ background: '#fff', padding: 40, borderRadius: 8,
        maxWidth: 480, textAlign: 'center', fontSize: 13 }}>
        <h2 style={{ color: '#020D7F', marginTop: 0 }}>Hydrodata Engenharia</h2>
        <div style={{ color: '#B91626' }}>⚠ Link inválido. Solicite um novo link.</div>
      </div>
    </div>
  );
}

// ─── Hostname especial: propostas.hydrodataeng.com.br/{hash} ─────
// Roteia direto pra PaginaPublicaProposta sem precisar de /publico/proposta.
// Mantido como early-return ANTES do BrowserRouter (não precisa dele).
function HostnamePropostaSwitch({ children }: { children: ReactNode }) {
  const host = window.location.hostname;
  if (host.startsWith('propostas.')) {
    const hashFromPath = window.location.pathname.replace(/^\/+|\/+$/g, '').split('/')[0];
    if (hashFromPath && /^[a-zA-Z0-9_-]+$/.test(hashFromPath)) {
      return <PaginaPublicaProposta hash={hashFromPath} />;
    }
    return <LinkInvalido />;
  }
  // Compat: hash route alternativa pra Drive (#/drive/{hash})
  const hashRoute = window.location.hash || '';
  const matchDriveHash = hashRoute.match(/^#\/drive\/([a-zA-Z0-9_-]+)\/?$/);
  if (matchDriveHash) {
    return <PaginaPublicaDrive hash={matchDriveHash[1]} />;
  }
  return <>{children}</>;
}

// ─── App raiz ─────────────────────────────────────────────────
// Estrutura:
//   <HostnamePropostaSwitch>        ← early-return pra propostas.* host
//     <BrowserRouter>
//       <ThemeProvider>             ← Amplify Authenticator theme
//         <Routes>
//           /publico/proposta/:hash  → PaginaPublicaProposta (sem auth)
//           /publico/cliente/:hash   → PaginaPublicaCliente (sem auth)
//           /publico/bm/:hash        → PaginaPublicaBm (sem auth)
//           /publico/drive/:hash     → PaginaPublicaDrive (sem auth)
//           /*                       → AppAutenticado (Cognito + shell)
//         </Routes>
//       </ThemeProvider>
//     </BrowserRouter>
//   </HostnamePropostaSwitch>
function App() {
  return (
    <HostnamePropostaSwitch>
      <BrowserRouter>
        <ThemeProvider theme={authTheme}>
          {/* [748bd5c0] Wrapper sem height — o próprio Authenticator (via CSS) ocupa
              100vh com flex center. Antes o wrapper minHeight:100vh + Auth com
              min-height:100vh criava scroll que tirava o centramento vertical. */}
          <div style={{ background: `linear-gradient(135deg, ${HD_BG} 0%, #E7ECFD 100%)` }}>
            <Routes>
              <Route path="/publico/proposta/:hash" element={<PublicoPropostaRoute />} />
              <Route path="/publico/cliente/:hash"  element={<PublicoClienteRoute />} />
              <Route path="/publico/bm/:hash"       element={<PublicoBmRoute />} />
              <Route path="/publico/drive/:hash"    element={<PublicoDriveRoute />} />
              <Route path="/publico/planejamento/:hash" element={<PublicoPlanejamentoRoute />} />
              <Route path="/leitura/:token" element={<PublicoEstacaoRoute />} />
              <Route path="/*" element={<AppAutenticado />} />
            </Routes>
          </div>
        </ThemeProvider>
      </BrowserRouter>
    </HostnamePropostaSwitch>
  );
}

// ─── AppAutenticado: shell + auth + páginas protegidas ────────
function AppAutenticado() {
  return (
    <>
      <Authenticator hideSignUp={true} components={authComponents} formFields={authFormFields}>
        {({ signOut, user }) => (
          <PermsProvider>
            <ShellAutenticado signOut={signOut} user={user} />
          </PermsProvider>
        )}
      </Authenticator>
      {/* fora do Authenticator: quando o Cognito recusa renovar, o próprio
          Authenticator volta para o login e desmontaria o aviso */}
      <AvisoSessaoExpirada />
    </>
  );
}

// ─── ShellAutenticado ─────────────────────────────────────────
// Toda a navegação interna (sidebar, header, conteúdo) vive aqui.
// Esse componente está dentro do <BrowserRouter>, então pode usar
// useNavigate / useLocation / NavLink livremente.
function ShellAutenticado({ signOut, user }: { signOut?: () => void; user: any }) {
  const navigate = useNavigate();
  const location = useLocation();
  const nexoPermitido = useNexoAcesso(user?.userId || user?.username || '');
  // Onda B2: matriz granular de permissoes (admin.perfis + permissoes_catalogo).
  // Coexiste com `abasPermitidas` antigo via fallback no userCanSee.
  const { pode, erro: permsErro } = usePerms();

  // 2026-06-09: patch global — evita fechar Modal Antd quando usuario
  // clica dentro, arrasta selecao pra fora e solta. Sem isso, o
  // mouseup fora era interpretado como click no backdrop -> close.
  // Resolve pros 41 modais sem mexer em cada um.
  useEffect(() => {
    let mouseDownInModal = false;
    const onMouseDown = (e: MouseEvent) => {
      const t = e.target as HTMLElement;
      mouseDownInModal = !!t?.closest?.('.ant-modal-content');
    };
    const onClickCapture = (e: MouseEvent) => {
      const t = e.target as HTMLElement;
      // ant-modal-wrap = backdrop. Se mousedown comecou dentro do modal,
      // bloqueia o click que iria disparar onCancel.
      if (mouseDownInModal && t && (
        t.classList?.contains('ant-modal-wrap') ||
        t.classList?.contains('ant-modal-mask')
      )) {
        e.stopPropagation();
        e.preventDefault();
      }
      mouseDownInModal = false;
    };
    document.addEventListener('mousedown', onMouseDown, true);
    document.addEventListener('click', onClickCapture, true);
    return () => {
      document.removeEventListener('mousedown', onMouseDown, true);
      document.removeEventListener('click', onClickCapture, true);
    };
  }, []);

  // ─── Permissões / grupos do Cognito ─────────────────────────
  const [pendentes, setPendentes] = useState<number>(0);
  const [groups, setGroups] = useState<string[] | null>(null); // null = carregando
  const [abasPermitidas, setAbasPermitidas] = useState<string[] | null>(null);
  const [debugInfo, setDebugInfo] = useState<any>(null);
  const [userNome, setUserNome] = useState<string>('');
  const [userFotoUrl, setUserFotoUrl] = useState<string>('');

  // ─── Responsivo: navbar lateral com colapso em mobile E desktop ──────
  const isMobile = useIsMobile();
  const [mobileNavOpened, { toggle: toggleMobileNav, close: closeMobileNav }] = useDisclosure(false);
  const [desktopNavOpened, { toggle: toggleDesktopNav }] = useDisclosure(
    localStorage.getItem('navbarColapsada') !== '1'
  );
  useEffect(() => {
    localStorage.setItem('navbarColapsada', desktopNavOpened ? '0' : '1');
  }, [desktopNavOpened]);

  // ─── Derivações a partir da URL ─────────────────────────────
  // A URL é a fonte de verdade pra qual aba/módulo está ativo.
  const abaAtiva = abaPorPath(location.pathname);
  const moduloAtivo: Modulo = abaAtiva?.modulo || 'home';

  // Qual módulo está expandido no sidebar — segue o módulo da URL atual,
  // mas o usuário pode expandir outro módulo (sem trocar a página).
  const [moduloExpandido, setModuloExpandido] = useState<Modulo | null>(moduloAtivo);
  useEffect(() => { setModuloExpandido(moduloAtivo); }, [moduloAtivo]);

  // Fecha o drawer mobile ao trocar de URL
  useEffect(() => { closeMobileNav(); }, [location.pathname, closeMobileNav]);

  // Persiste última URL visitada (substitui app:pagina/app:modulo).
  // Em login subsequente, redireciona pra essa URL se o user ainda pode acessar.
  useEffect(() => {
    if (location.pathname && location.pathname !== '/login') {
      try { localStorage.setItem('app:ultima-url', location.pathname + location.search); } catch {}
    }
  }, [location.pathname, location.search]);

  // ─── Carrega grupos do ID token ─────────────────────────────
  useEffect(() => {
    async function tentarExtrair(session: any) {
      const idPayload: any = session.tokens?.idToken?.payload || {};
      const idStr = session.tokens?.idToken?.toString() || '';
      const accessPayload: any = session.tokens?.accessToken?.payload || {};
      const accessStr = session.tokens?.accessToken?.toString() || '';
      let idDecoded: any = {};
      if (idStr && idStr.split('.').length === 3) {
        try {
          const b64 = idStr.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
          idDecoded = JSON.parse(atob(b64 + '='.repeat((4 - b64.length % 4) % 4)));
        } catch {}
      }
      let accessDecoded: any = {};
      if (accessStr && accessStr.split('.').length === 3) {
        try {
          const b64 = accessStr.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
          accessDecoded = JSON.parse(atob(b64 + '='.repeat((4 - b64.length % 4) % 4)));
        } catch {}
      }
      const candidatos = [idDecoded, idPayload, accessDecoded, accessPayload];
      const melhor = candidatos.reduce((a, b) => Object.keys(b).length > Object.keys(a).length ? b : a, {});
      return { payload: melhor, idStr, accessStr };
    }

    function lerLocalStorageTokens() {
      try {
        const keys = Object.keys(localStorage).filter(k => k.includes('CognitoIdentityServiceProvider'));
        const byType: any = {};
        for (const k of keys) {
          if (k.endsWith('.idToken')) byType.idToken = localStorage.getItem(k) || '';
          if (k.endsWith('.accessToken')) byType.accessToken = localStorage.getItem(k) || '';
          if (k.endsWith('.LastAuthUser')) byType.lastUser = localStorage.getItem(k) || '';
        }
        return { keys_encontradas: keys.length, ...byType };
      } catch (e: any) {
        return { erro_storage: e?.message || String(e) };
      }
    }

    async function carregarGrupos() {
      const tentativas: any[] = [];
      const lsInfo = lerLocalStorageTokens();

      async function tentar(metodo: string, forceRefresh: boolean, wait = 0) {
        if (wait) await new Promise(r => setTimeout(r, wait));
        try {
          const session = await fetchAuthSession(forceRefresh ? { forceRefresh: true } : undefined);
          const r = await tentarExtrair(session);
          tentativas.push({ metodo, ok: !!r.idStr || !!r.accessStr, claims: Object.keys(r.payload).length });
          return r;
        } catch (e: any) {
          tentativas.push({ metodo, erro: e?.message || String(e) });
          return { payload: {}, idStr: '', accessStr: '' };
        }
      }

      let { payload, idStr, accessStr } = await tentar('normal', false);
      if (!idStr && !accessStr) ({ payload, idStr, accessStr } = await tentar('retry-500ms', false, 500));
      if (!idStr && !accessStr) ({ payload, idStr, accessStr } = await tentar('retry-2s', false, 2000));
      if (!idStr && !accessStr) ({ payload, idStr, accessStr } = await tentar('forceRefresh', true));

      // último recurso: o idToken cru do localStorage — mas nunca um já vencido
      // (mandá-lo é o que fazia "Network error" em tudo; o 401 do API Gateway sai sem CORS)
      const expLs = claimsDoJwt(lsInfo.idToken)?.exp;
      if (!idStr && lsInfo.idToken && lsInfo.idToken.split('.').length === 3 && !(typeof expLs === 'number' && expLs * 1000 < Date.now())) {
        try {
          const b64 = lsInfo.idToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
          payload = JSON.parse(atob(b64 + '='.repeat((4 - b64.length % 4) % 4)));
          idStr = lsInfo.idToken;
          tentativas.push({ metodo: 'localStorage-direto', ok: true, claims: Object.keys(payload).length });
        } catch (e: any) {
          tentativas.push({ metodo: 'localStorage-direto', erro: e?.message });
        }
      }

      const raw = payload['cognito:groups'];
      const arr = Array.isArray(raw) ? raw as string[]
                : typeof raw === 'string' ? raw.split(/[\s,]+/).filter(Boolean)
                : [];
      setGroups(arr);

      const nome = (payload.name || '').trim()
        || (payload.email || '').split('@')[0]
        || '';
      setUserNome(nome);

      if (arr.length > 0 && idStr) {
        try {
          const res = await fetch(`${API_URL}/meu-perfil`, { headers: { Authorization: idStr } });
          if (res.ok) {
            const data = await res.json();
            const body = data.body ? JSON.parse(data.body) : data;
            if (Array.isArray(body.abas)) setAbasPermitidas(body.abas);
          }
        } catch { /* usa fallback hardcoded */ }
      } else {
        setAbasPermitidas(null);
      }
      setDebugInfo({
        email: payload.email || payload['cognito:username'] || '?',
        sub: payload.sub || '?',
        cognito_groups_raw: raw,
        cognito_groups_type: Array.isArray(raw) ? 'array' : typeof raw,
        all_claims_keys: Object.keys(payload),
        token_id_len: idStr.length,
        token_access_len: accessStr.length,
        localStorage_info: lsInfo,
        tentativas,
      });
    }
    carregarGrupos();

    const unsub = Hub.listen('auth', ({ payload }) => {
      if (payload.event === 'signedOut') {
        setGroups(null);
        setDebugInfo(null);
      }
      if (payload.event === 'signedIn' || payload.event === 'tokenRefresh') {
        carregarGrupos();
      }
    });
    return unsub;
  }, []);

  // ─── Conta categorias pendentes (badge no sidebar) ──────────
  useEffect(() => {
    async function contar() {
      try {
        const session = await fetchAuthSession();
        const token = session.tokens?.idToken?.toString() ?? '';
        const res = await axios.get(`${API_URL}/categorias`, { headers: { Authorization: token } });
        const data = res.data.body ? JSON.parse(res.data.body) : res.data;
        setPendentes(Array.isArray(data) ? data.filter((c: any) => c.status === 'pendente').length : 0);
      } catch {}
    }
    contar();
  }, [location.pathname]);

  // Badge de reembolsos pendentes migrou pra ABA "Reembolsos Campo" dentro do
  // hub Pagamentos (pedido do Lucas 2026-07-27) — contagem vive no próprio hub.

  // ─── Foto do usuário pro avatar do header ───────────────────
  useEffect(() => {
    if (!userNome) return;
    let cancelled = false;
    (async () => {
      try {
        const session = await fetchAuthSession();
        const token = session.tokens?.idToken?.toString() ?? '';
        const payload: any = session.tokens?.idToken?.payload || {};
        const meuEmail = (payload.email || '').toLowerCase();
        if (!meuEmail) return;
        const r = await axios.get(`${API_URL}/admin/usuarios`, { headers: { Authorization: token } });
        const lista: any[] = r.data.body ? JSON.parse(r.data.body) : r.data;
        const meu = lista.find((u: any) => (u.email || '').toLowerCase() === meuEmail);
        if (!cancelled && meu?.foto_url) setUserFotoUrl(meu.foto_url);
      } catch { /* silencioso — mostra avatar com iniciais */ }
    })();
    return () => { cancelled = true; };
  }, [userNome, location.pathname]);

  // ─── Navegação programática via window event (compat legado) ─
  // Páginas internas disparam `app-navigate` com { modulo, pagina } —
  // mapeamos pra URL via PAGE_ID_TO_PATH e usamos navigate().
  useEffect(() => {
    function handler(e: any) {
      const { pagina: p } = e.detail || {};
      const path = p && PAGE_ID_TO_PATH[p];
      if (path) navigate(path);
    }
    window.addEventListener('app-navigate', handler);
    return () => window.removeEventListener('app-navigate', handler);
  }, [navigate]);

  // ─── Ordem custom da sidebar (drag-drop antigo persistido) ──
  const ordemPadrao: Record<string, string[]> = {};
  for (const a of ABAS) {
    if (!ordemPadrao[a.modulo]) ordemPadrao[a.modulo] = [];
    ordemPadrao[a.modulo].push(a.id);
  }
  const tabPrefsFin = useTabsPrefs<string>('tabPrefs.fin', ordemPadrao['financeiro']     || []);
  const tabPrefsCom = useTabsPrefs<string>('tabPrefs.com', ordemPadrao['comercial']      || []);
  const tabPrefsOp  = useTabsPrefs<string>('tabPrefs.op',  ordemPadrao['operacional']    || []);
  const tabPrefsAdministr = useTabsPrefs<string>('tabPrefs.administrativo', ordemPadrao['administrativo'] || []);
  const tabPrefsJur = useTabsPrefs<string>('tabPrefs.juridico', ordemPadrao['juridico']   || []);
  const tabPrefsAdm = useTabsPrefs<string>('tabPrefs.adm', ordemPadrao['admin']          || []);
  const tabPrefsAtv = useTabsPrefs<string>('tabPrefs.atv', ordemPadrao['atividades']     || []);
  function getTabPrefs(modulo: string) {
    if (modulo === 'financeiro')      return tabPrefsFin;
    if (modulo === 'comercial')       return tabPrefsCom;
    if (modulo === 'operacional')     return tabPrefsOp;
    if (modulo === 'administrativo')  return tabPrefsAdministr;
    if (modulo === 'juridico')        return tabPrefsJur;
    if (modulo === 'admin')           return tabPrefsAdm;
    if (modulo === 'atividades')      return tabPrefsAtv;
    return tabPrefsOp;
  }

  // Até carregar os grupos, mostra tela de carregamento (fail-secure)
  if (groups === null) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: HD_BLUE, fontSize: 13 }}>
        Carregando perfil...
      </div>
    );
  }
  const grps = groups;
  const modulosVisiveis = MODULOS.filter(m => m.id === 'nexo'
    ? nexoPermitido : userCanSeeModulo(m.id, grps, abasPermitidas, ABAS, pode, permsErro));

  // Usuário sem grupos ou sem nenhum módulo visível → mostra mensagem + botão sair
  if (modulosVisiveis.length === 0) {
    return <TelaSemAcesso signOut={signOut} grps={grps} user={user} debugInfo={debugInfo} />;
  }

  const sair = async () => {
    try { signOut?.(); } catch {}
    try { localStorage.clear(); sessionStorage.clear(); } catch {}
    window.location.href = '/';
  };

  return (
    <AppShell
      header={{ height: 56 }}
      navbar={{
        width: 264,
        breakpoint: 'sm',
        collapsed: { mobile: !mobileNavOpened, desktop: !desktopNavOpened },
      }}
      padding={0}
      styles={{
        main: { background: HD_BG, minHeight: '100vh' },
        header: { borderBottom: `1px solid ${HD_BLUE_DEEP}14`, background: '#fff' },
        navbar: { borderRight: `1px solid ${HD_BLUE_DEEP}14`, background: '#fff' },
      }}
    >
      {/* ─── Header (topo): logo + título da página + sino + sair ─── */}
      <AppShell.Header>
        <Group h="100%" px="md" gap="sm" wrap="nowrap" style={{ height: '100%' }}>
          <Burger
            opened={mobileNavOpened}
            onClick={toggleMobileNav}
            hiddenFrom="sm"
            size="sm"
            color={HD_BLUE}
            aria-label={mobileNavOpened ? 'Fechar menu' : 'Abrir menu'}
          />
          <Burger
            opened={desktopNavOpened}
            onClick={toggleDesktopNav}
            visibleFrom="sm"
            size="sm"
            color={HD_BLUE}
            aria-label={desktopNavOpened ? 'Recolher navbar' : 'Abrir navbar'}
          />
          <img
            src="/hydrodata-logo.png"
            alt="Hydrodata"
            style={{ height: 28, width: 'auto', display: 'block', flexShrink: 0 }}
          />
          {!isMobile ? (
            <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.15 }}>
              <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.22em',
                             color: HD_BLUE_DEEP, textTransform: 'uppercase' }}>
                Hydrodata
              </span>
              <span style={{ fontSize: 12, fontWeight: 500, color: HD_BLUE,
                             letterSpacing: '0.04em' }}>
                Gestão Empresarial
              </span>
            </div>
          ) : (
            <span style={{ fontSize: 12, fontWeight: 600, color: HD_BLUE_DEEP,
                           overflow: 'hidden', textOverflow: 'ellipsis',
                           whiteSpace: 'nowrap', minWidth: 0 }}>
              {abaAtiva?.label || 'Hydrodata'}
            </span>
          )}
          <div style={{ flex: 1 }} />
          {/* HORAS (2026-08-21): anotar de onde a pessoa estiver. Ir até a tela
              é um passo, e passo é o que faz deixar para depois — "depois" é o
              motivo de a cobertura estar em 41-85%. O ponto no relógio avisa
              que o dia de hoje está em aberto. */}
          {pode('horas.apontar') && (
            <HorasAtalho onAbrirSemana={() => navigate('/operacional/horas')} />
          )}
          {/* Bom-dia das horas (2026-10-01): na primeira mexida do dia, sugere por
              onde começar (centro de custo do setor ou o mais usado em 7 dias) e
              liga o cronômetro num clique. "Escolher outro" abre o atalho acima. */}
          {pode('horas.apontar') && (
            <HorasBomDia onEscolher={() => window.dispatchEvent(new Event(EVENTO_ABRIR_ATALHO))} />
          )}
          <NotificacoesBell onIrPara={(_m, p, id) => {
            // Deep-link: grava o id no sessionStorage com chave específica
            // por página de destino, então navega via URL.
            if (id) {
              const chaves: Record<string, string> = {
                'atividades': 'atividades_abrir_id',
                'com-pipeline': 'pipeline_destacar_proposta_id',
                'op-contratos': 'contratos_destacar_id',
                'mkt-posts': 'mkt_posts_abrir_id',
                // Nível 2 — abrir o item específico ao chegar pelo sino:
                'adm-tickets': 'tickets_abrir_id',
                'ativ-requisicoes': 'requisicoes_abrir_id',
                'ativ-reembolsos': 'reembolsos_abrir_id',
                'fin-reembolsos-campo': 'reembolsos_campo_abrir_id',
                'fin-combustiveis': 'combustiveis_abrir_id',
                'fin-contas-pagar': 'cp_abrir_lancamento_id',
                // Menção em comentário de parcela do CR (2026-09-02)
                'fin-contas-receber': 'cr_abrir_parcela_id',
                // Radar de licitações (2026-09-22): o sino do fim de prazo
                // abre a oportunidade em vez de largar a pessoa na lista.
                'com-hub-licitacoes': 'licitacoes_abrir_oportunidade_id',
                // Meu Radar: o sino do lembrete livre abre o item. O id novo
                // (24/09/2026) e o antigo, dos sinos gravados antes da mudança
                // — os dois caem em /comercial/radar e o MeuRadar lê a chave.
                'com-meu-radar': 'radar_abrir_item_id',
                'adm-meu-radar': 'radar_abrir_item_id',
                // WEEKLY: sino do briefing abre a reunião direto
                'op-reunioes': 'op-reunioes',
                // REUNIÃO GERAL: sino do rascunho (segunda) e do publicado
                'op-reuniao-geral': 'op-reuniao-geral',
              };
              const k = chaves[p];
              if (k) sessionStorage.setItem(k, String(id));
            }
            const path = PAGE_ID_TO_PATH[p];
            if (path) navigate(path);
          }} />
          {/* Menu do usuário — avatar + nome + dropdown com Meu Perfil/Sair */}
          <Menu shadow="md" width={220} position="bottom-end" withArrow>
            <Menu.Target>
              <UnstyledButton aria-label="Menu do usuário"
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '4px 8px', borderRadius: 6,
                }}>
                <Avatar src={userFotoUrl || undefined} size={32} radius="xl"
                  color="brand">
                  {(userNome || '?').split(' ').map(s => s[0]).slice(0, 2).join('').toUpperCase()}
                </Avatar>
                {!isMobile && (
                  <div style={{ display: 'flex', flexDirection: 'column',
                                alignItems: 'flex-start', lineHeight: 1.15 }}>
                    <span style={{ fontSize: 12, color: '#020D7F', fontWeight: 600 }}>
                      {userNome || user?.signInDetails?.loginId || '...'}
                    </span>
                    <span style={{ fontSize: 9, color: '#888', fontFamily: 'monospace' }}>
                      {grps.length > 0 ? grps.join(' · ') : 'sem grupos'}
                    </span>
                  </div>
                )}
                {!isMobile && <IconChevronDown size={14} color="#6A7092" />}
              </UnstyledButton>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Label>
                {user?.signInDetails?.loginId}
              </Menu.Label>
              <Menu.Item leftSection={<IconUser size={16} />}
                onClick={() => navigate('/perfil')}>
                Meu perfil
              </Menu.Item>
              <Menu.Divider />
              <Menu.Item leftSection={<IconLogout size={16} />} color="red"
                onClick={sair}>
                Sair
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Group>
      </AppShell.Header>

      {/* ─── Navbar (esquerda): módulos colapsáveis com subpáginas ─── */}
      <AppShell.Navbar p="xs">
        <AppShell.Section grow component={ScrollArea} type="auto" scrollbarSize={6}>
          {modulosVisiveis.map((m) => {
            const abasDoModulo = ABAS.filter(a => a.modulo === m.id
              && (a.id === 'nexo' ? nexoPermitido : userCanSee(a, grps, abasPermitidas, pode, permsErro)));
            if (abasDoModulo.length === 0) return null;
            const prefsDoModulo = getTabPrefs(m.id);
            const idxMap: Record<string, number> = {};
            prefsDoModulo.ordem.forEach((id, i) => { idxMap[id] = i; });
            const abasOrdenadas = [...abasDoModulo].sort((a, b) =>
              (idxMap[a.id] ?? 999) - (idxMap[b.id] ?? 999));

            const icone = ICONE_DO_MODULO[m.id];

            // Home: link direto (sem expansão — só tem uma página)
            if (m.id === 'home') {
              const aba = abasOrdenadas[0];
              const ativo = !!matchPath({ path: aba.path, end: true }, location.pathname);
              return (
                <MantineNavLink
                  key={m.id}
                  label={<span style={{ fontWeight: 500 }}>{m.label}</span>}
                  leftSection={icone}
                  active={ativo}
                  color="brand"
                  variant="light"
                  component={RouterNavLink}
                  to={aba.path}
                  end
                  style={{ borderRadius: 6, marginBottom: 4 }}
                />
              );
            }

            const eModuloAtivo = moduloAtivo === m.id;
            return (
              <MantineNavLink
                key={m.id}
                label={<span style={{ fontWeight: eModuloAtivo ? 600 : 500,
                                       color: eModuloAtivo ? HD_BLUE_DEEP : undefined }}>
                  {m.label}
                </span>}
                leftSection={icone}
                opened={moduloExpandido === m.id}
                onClick={() => setModuloExpandido(prev => prev === m.id ? null : m.id)}
                childrenOffset={30}
                rightSection={
                  <IconChevronRight
                    size={14}
                    stroke={1.5}
                    style={{
                      transform: moduloExpandido === m.id ? 'rotate(90deg)' : 'none',
                      transition: 'transform 150ms ease',
                    }}
                  />
                }
                style={{
                  borderRadius: 6,
                  marginBottom: 2,
                  background: eModuloAtivo ? '#F0F3FF' : undefined,
                }}
              >
                {abasOrdenadas.map(aba => {
                  const ativo = !!matchPath({ path: aba.path, end: true }, location.pathname);
                  // FIN-HUBS: badge de categorias → Cadastros. O de reembolsos
                  // agora fica na própria aba do hub Pagamentos (2026-07-27).
                  const badgeCount = aba.id === 'fin-cadastros' ? pendentes : 0;
                  const showBadge = badgeCount > 0;
                  return (
                    <MantineNavLink
                      key={aba.id}
                      label={<span style={{ fontSize: 13 }}>{aba.label}</span>}
                      // Mesmo tratamento do módulo: o ícone vai no
                      // `leftSection`, e não dentro do texto. É o que faz a
                      // lista alinhar — emoji no meio do rótulo empurra cada
                      // linha por uma largura diferente.
                      leftSection={ICONE_DA_PAGINA[aba.id]}
                      active={ativo}
                      color="brand"
                      variant="light"
                      rightSection={showBadge ? (
                        <Badge size="xs" color="red" variant="filled" circle>
                          {badgeCount}
                        </Badge>
                      ) : undefined}
                      component={RouterNavLink}
                      to={aba.path}
                      end
                      style={{ borderRadius: 6, paddingTop: 6, paddingBottom: 6 }}
                    />
                  );
                })}
              </MantineNavLink>
            );
          })}
        </AppShell.Section>

        {/* Rodapé fixo: usuário + grupos */}
        <AppShell.Section
          p="xs"
          style={{ borderTop: `1px solid ${HD_BLUE_DEEP}14`, marginTop: 6 }}>
          <div style={{ fontSize: 11, color: '#444', fontWeight: 500,
                        overflow: 'hidden', textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap' }}>
            {user?.signInDetails?.loginId}
          </div>
          <div style={{ fontSize: 9, color: '#999', fontFamily: 'monospace',
                        marginTop: 2 }}>
            {grps.length > 0 ? grps.join(' · ') : 'sem grupos'}
          </div>
        </AppShell.Section>
      </AppShell.Navbar>

      <AppShell.Main>
        <Routes>
          {/* Visão Geral (home) */}
          <Route path="/" element={
            <HomeDashboard
              groups={grps}
              abasPermitidas={abasPermitidas}
              onIrPara={(_m, p) => {
                const path = PAGE_ID_TO_PATH[p];
                if (path) navigate(path);
              }}
            />
          } />

          {/* ─── Módulo Financeiro — FIN-HUBS (2026-07-13) ─── */}
          <Route path="/financeiro/dre-hub"           element={<P pageId="fin-hub-dre"         groups={G_FIN_VIEW} userGroups={grps} abasPermitidas={abasPermitidas}><FinanceiroDreHub /></P>} />
          <Route path="/financeiro/recebimentos"      element={<P pageId="fin-recebimentos"    groups={G_FIN_VIEW} userGroups={grps} abasPermitidas={abasPermitidas}><FinanceiroRecebimentosHub /></P>} />
          <Route path="/financeiro/pagamentos"        element={<P pageId="fin-pagamentos"      groups={G_FIN_VIEW} userGroups={grps} abasPermitidas={abasPermitidas}><FinanceiroPagamentosHub /></P>} />
          <Route path="/financeiro/cadastros"         element={<P pageId="fin-cadastros"       groups={G_FIN_VIEW} userGroups={grps} abasPermitidas={abasPermitidas}><FinanceiroCadastrosHub /></P>} />
          <Route path="/financeiro/fluxo-caixa"       element={<P pageId="fin-fluxo-caixa"     groups={G_FIN_VIEW} userGroups={grps} abasPermitidas={abasPermitidas}><FinanceiroFluxoCaixa /></P>} />
          <Route path="/financeiro/extrato-bancario"  element={<P pageId="fin-extrato-bancario" groups={G_FIN_EDIT} userGroups={grps} abasPermitidas={abasPermitidas}><FinanceiroExtratoBancario /></P>} />
          <Route path="/financeiro/atividades"        element={<P pageId="fin-atividades"      groups={G_FIN_VIEW} userGroups={grps} abasPermitidas={abasPermitidas}><Atividades moduloFixo="financeiro" onIrParaOrigem={() => {}} /></P>} />
          {/* Rotas antigas → redirects que preservam a query (deep-links ?parcela= etc.) */}
          <Route path="/financeiro/dre"               element={<RedirectHub hub="/financeiro/dre-hub"      aba="dre" />} />
          <Route path="/financeiro/orcamento"         element={<RedirectHub hub="/financeiro/dre-hub"      aba="orcamento" />} />
          <Route path="/financeiro/lancamentos"       element={<RedirectHub hub="/financeiro/dre-hub"      aba="lancamentos" />} />
          <Route path="/financeiro/rateio-clockify"   element={<RedirectHub hub="/financeiro/dre-hub"      aba="rateio-clockify" />} />
          <Route path="/financeiro/contas-receber"    element={<RedirectHub hub="/financeiro/recebimentos" aba="contas-receber" />} />
          <Route path="/financeiro/medicoes"          element={<RedirectHub hub="/financeiro/recebimentos" aba="medicoes" />} />
          <Route path="/financeiro/deliberacao"       element={<RedirectHub hub="/financeiro/recebimentos" aba="deliberacao" />} />
          <Route path="/financeiro/contas-pagar"      element={<RedirectHub hub="/financeiro/pagamentos"   aba="contas-pagar" />} />
          <Route path="/financeiro/cartoes"           element={<RedirectHub hub="/financeiro/pagamentos"   aba="cartoes" />} />
          <Route path="/financeiro/reembolsos-campo"  element={<RedirectHub hub="/financeiro/pagamentos"   aba="reembolsos-campo" />} />
          <Route path="/financeiro/combustiveis"      element={<RedirectHub hub="/financeiro/pagamentos"   aba="combustiveis" />} />
          <Route path="/financeiro/emprestimos"       element={<RedirectHub hub="/financeiro/pagamentos"   aba="emprestimos" />} />
          <Route path="/financeiro/diferimentos"      element={<RedirectHub hub="/financeiro/pagamentos"   aba="diferimentos" />} />
          <Route path="/financeiro/categorias"        element={<RedirectHub hub="/financeiro/cadastros"    aba="categorias" />} />
          <Route path="/financeiro/centros-custo"     element={<RedirectHub hub="/financeiro/cadastros"    aba="centros-custo" />} />
          {/* 24/08: Profissionais mudou pro hub RH do Administrativo. O path
              antigo continua vivo — link salvo, sino e app-navigate chegam por
              aqui e caem na aba certa do endereço novo. */}
          <Route path="/financeiro/profissionais"     element={<RedirectHub hub="/administrativo/rh"       aba="profissionais" />} />
          {/* Onda 0 (2026-07-02): ReconcileNotion aposentado — diagnóstico vive em Admin > Integrações */}
          <Route path="/financeiro/reconcile-notion"  element={<Navigate to="/admin/integracoes?aba=notion" replace />} />

          {/* ─── Módulo Comercial — COM-HUBS (2026-08-21) ─── */}
          <Route path="/comercial"             element={<P pageId="com-painel"         groups={G_COM_VIEW} userGroups={grps} abasPermitidas={abasPermitidas}><ComercialPainelHub /></P>} />
          <Route path="/comercial/leads"       element={<P pageId="com-leads"          groups={G_COM_VIEW} userGroups={grps} abasPermitidas={abasPermitidas}><ComercialLeads /></P>} />
          <Route path="/comercial/pipeline"    element={<P pageId="com-pipeline"       groups={G_COM_VIEW} userGroups={grps} abasPermitidas={abasPermitidas}><ComercialPipeline /></P>} />
          <Route path="/comercial/followups"   element={<P pageId="com-followups"      groups={G_COM_VIEW} userGroups={grps} abasPermitidas={abasPermitidas}><ComercialFollowupsHub /></P>} />
          <Route path="/comercial/licitacoes"  element={<P pageId="com-hub-licitacoes" groups={G_COM_EDIT} userGroups={grps} abasPermitidas={abasPermitidas}><ComercialLicitacoesHub /></P>} />
          <Route path="/comercial/clientes"    element={<P pageId="com-hub-clientes"   groups={G_COM_EDIT} userGroups={grps} abasPermitidas={abasPermitidas}><ComercialClientesHub /></P>} />
          <Route path="/comercial/propostas"   element={<P pageId="com-hub-propostas"  groups={G_COM_EDIT} userGroups={grps} abasPermitidas={abasPermitidas}><ComercialPropostasHub /></P>} />
          <Route path="/comercial/comissoes"   element={<P pageId="com-hub-comissoes"  groups={G_ADMINISTRATIVO_VIEW} userGroups={grps} abasPermitidas={abasPermitidas}><ComercialComissoesHub /></P>} />
          <Route path="/comercial/radar"       element={<P pageId="com-meu-radar"      groups={G_COM_VIEW} userGroups={grps} abasPermitidas={abasPermitidas}><ComercialRadarHub /></P>} />
          {/* Paths antigos → hub + aba, preservando a query (deep-links). */}
          <Route path="/comercial/metas"       element={<RedirectHub hub="/comercial"             aba="metas" />} />
          <Route path="/comercial/atestados"   element={<RedirectHub hub="/comercial/licitacoes"  aba="atestados" />} />
          <Route path="/comercial/contatos"    element={<RedirectHub hub="/comercial/clientes"    aba="contatos" />} />
          <Route path="/comercial/composicoes" element={<RedirectHub hub="/comercial/propostas"   aba="composicoes" />} />
          <Route path="/comercial/custos"      element={<RedirectHub hub="/comercial/propostas"   aba="custos" />} />
          <Route path="/comercial/indicacoes"  element={<RedirectHub hub="/comercial/comissoes"   aba="indicacoes" />} />
          <Route path="/comercial/hydra"       element={<P pageId="com-hydra"      groups={G_COM_EDIT} userGroups={grps} abasPermitidas={abasPermitidas}><HydraConfig /></P>} />

          {/* ─── Módulo Operacional ─── */}
          <Route path="/operacional/contratos"  element={<P pageId="op-contratos"  groups={G_OP_VIEW} userGroups={grps} abasPermitidas={abasPermitidas}><OperacionalContratos /></P>} />
          <Route path="/operacional/mapa-geral" element={<RedirectHub hub="/operacional/contratos" aba="mapa" param="vista" />} />
          <Route path="/operacional/jim"        element={<P pageId="op-jim"        groups={G_OP_EDIT} userGroups={grps} abasPermitidas={abasPermitidas}><JimConfig /></P>} />
          <Route path="/operacional/horas" element={<P pageId="op-horas" groups={G_OP_VIEW} userGroups={grps} abasPermitidas={abasPermitidas}><Horas /></P>} />
          <Route path="/operacional/campo" element={<P pageId="op-campo" groups={G_OP_VIEW} userGroups={grps} abasPermitidas={abasPermitidas}><OperacionalCampoHub /></P>} />
          <Route path="/operacional/diario-campo" element={<P pageId="op-diario-campo" groups={G_OP_VIEW} userGroups={grps} abasPermitidas={abasPermitidas}><OperacionalDiarioCampo /></P>} />
          <Route path="/operacional/dados-campo" element={<P pageId="op-dados-campo" groups={G_OP_VIEW} userGroups={grps} abasPermitidas={abasPermitidas}><OperacionalDadosCampo /></P>} />
          <Route path="/operacional/controle-entregas" element={<P pageId="op-controle-entregas" groups={G_OP_VIEW} userGroups={grps} abasPermitidas={abasPermitidas}><OperacionalControleEntregas /></P>} />
          <Route path="/operacional/planejamento-campo" element={<P pageId="op-planejamento-campo" groups={G_OP_VIEW} userGroups={grps} abasPermitidas={abasPermitidas}><PlanejamentoCampo /></P>} />
          <Route path="/operacional/kits" element={<P pageId="op-kits" groups={G_OP_VIEW} userGroups={grps} abasPermitidas={abasPermitidas}><KitsCampo /></P>} />
          <Route path="/operacional/reunioes" element={<P pageId="op-reunioes" groups={G_OP_VIEW} userGroups={grps} abasPermitidas={abasPermitidas}><OperacionalReunioes /></P>} />
          {/* groups={[]} igual ao item do menu: quem porteia é a perm + o backend */}
          <Route path="/operacional/reuniao-geral" element={<P pageId="op-reuniao-geral" groups={[]} userGroups={grps} abasPermitidas={abasPermitidas}><OperacionalReuniaoGeral /></P>} />
          <Route path="/operacional/monitoramento" element={<P pageId="op-monitoramento" groups={G_OP_VIEW} userGroups={grps} abasPermitidas={abasPermitidas}><OperacionalMonitoramentoHub /></P>} />
          {/* Monitoramento (2026-08-21): os 2 paths antigos viram redirect pro
              hub com a aba certa, preservando a query — deep-link de estação
              (?estacao=) e link de notificação do portal seguem valendo. */}
          <Route path="/operacional/estacoes" element={<RedirectHub hub="/operacional/monitoramento" aba="estacoes" />} />
          <Route path="/operacional/portal-clientes" element={<RedirectHub hub="/operacional/monitoramento" aba="portal" />} />

          {/* ─── Módulo Administrativo ─── */}
          <Route path="/administrativo/patrimonio"          element={<P pageId="adm-patrimonio"          groups={G_ADMINISTRATIVO_VIEW} userGroups={grps} abasPermitidas={abasPermitidas}><Patrimonio /></P>} />
          <Route path="/administrativo/estoque"             element={<P pageId="adm-estoque"             groups={G_ADMINISTRATIVO_VIEW} userGroups={grps} abasPermitidas={abasPermitidas}><Estoque /></P>} />
          {/* Meu Radar (24/09/2026): virou Comercial › Meu Radar. O path
              antigo redireciona PRESERVANDO a query — o `?item=` dos e-mails
              já enviados e os links salvos continuam abrindo o item. */}
          <Route path="/meu-radar"                          element={<RedirectHub hub="/comercial/radar" aba="radar" />} />
          <Route path="/administrativo/atividades"          element={<P pageId="adm-atividades"          groups={G_ADMINISTRATIVO_VIEW} userGroups={grps} abasPermitidas={abasPermitidas}><Atividades moduloFixo="administrativo" onIrParaOrigem={() => {}} /></P>} />
          <Route path="/administrativo/pam"                 element={<P pageId="adm-pam"                 groups={G_ADMINISTRATIVO_EDIT} userGroups={grps} abasPermitidas={abasPermitidas}><PamConfig /></P>} />
          <Route path="/administrativo/tickets"             element={<P pageId="adm-tickets"             groups={G_ADMINISTRATIVO_VIEW} userGroups={grps} abasPermitidas={abasPermitidas}><AdministrativoTickets /></P>} />
          {/* EXPEDIENTES: acervo + editor. O :id usa o MESMO pageId — o gate de
              conteúdo é por documento (envolvidos), resolvido no backend. */}
          <Route path="/administrativo/expedientes"         element={<P pageId="adm-expedientes"         groups={[]} userGroups={grps} abasPermitidas={abasPermitidas}><AdministrativoExpedientes /></P>} />
          <Route path="/administrativo/expedientes/:id"     element={<P pageId="adm-expedientes"         groups={[]} userGroups={grps} abasPermitidas={abasPermitidas}><EditorExpediente /></P>} />
          <Route path="/administrativo/documentos"          element={<P pageId="adm-documentos"          groups={G_ADMINISTRATIVO_VIEW} userGroups={grps} abasPermitidas={abasPermitidas}><AdministrativoDocumentos /></P>} />
          <Route path="/administrativo/rh"                  element={<P pageId="adm-rh"                  groups={G_RH_VIEW} userGroups={grps} abasPermitidas={abasPermitidas}><AdministrativoRHHub /></P>} />
          <Route path="/administrativo/horas"               element={<P pageId="adm-horas"               groups={G_ADMIN} userGroups={grps} abasPermitidas={abasPermitidas}><HorasDaEquipe /></P>} />
          <Route path="/administrativo/plano-acao"          element={<P pageId="adm-plano-acao"          groups={G_ADMIN} userGroups={grps} abasPermitidas={abasPermitidas}><PlanoDeAcao /></P>} />
          {/* Compat com URL antiga: /patrimonio redireciona pra adm-patrimonio */}
          <Route path="/patrimonio" element={<P pageId="patrimonio" groups={G_ADMINISTRATIVO_VIEW} userGroups={grps} abasPermitidas={abasPermitidas}><Patrimonio /></P>} />

          {/* ─── Módulo Jurídico ─── */}
          <Route path="/juridico/contratos" element={<P pageId="jur-contratos" groups={G_JURIDICO_VIEW} userGroups={grps} abasPermitidas={abasPermitidas}><JuridicoContratos /></P>} />

          {/* ─── Módulo Marketing ─── */}
          <Route path="/marketing/posts" element={<P pageId="mkt-posts" groups={G_MARKETING} userGroups={grps} abasPermitidas={abasPermitidas}><Marketing /></P>} />

          {/* ─── Módulo Atividades (cross-módulo) ─── */}
          <Route path="/atividades" element={
            <Atividades onIrParaOrigem={(modulo, _tipo, origemId, _refId) => {
              if (modulo === 'comercial')          navigate('/comercial/pipeline');
              else if (modulo === 'operacional')   navigate('/operacional/contratos');
              else if (modulo === 'administrativo')navigate('/administrativo/estoque');
              else if (modulo === 'gestao')         navigate('/administrativo/plano-acao');
              else if (modulo === 'financeiro')    navigate('/financeiro/dre');
              else if (modulo === 'marketing') {
                if (origemId) sessionStorage.setItem('mkt_posts_abrir_id', String(origemId));
                navigate('/marketing/posts');
              }
              void origemId;
            }} />
          } />
          {/* ADM-1 (2026-06-12): sub-módulos do Atividades — toda a equipe */}
          <Route path="/atividades/requisicoes" element={<P pageId="ativ-requisicoes" groups={[]} userGroups={grps} abasPermitidas={abasPermitidas}><AtividadesRequisicoes /></P>} />
          <Route path="/atividades/reembolsos"  element={<P pageId="ativ-reembolsos"  groups={[]} userGroups={grps} abasPermitidas={abasPermitidas}><AtividadesReembolsos /></P>} />
          <Route path="/atividades/ouvidoria"   element={<P pageId="ativ-ouvidoria"   groups={[]} userGroups={grps} abasPermitidas={abasPermitidas}><AtividadesOuvidoria /></P>} />

          {/* ─── Módulo Admin ─── */}
          <Route path="/nexo" element={<Nexo />} />
          <Route path="/admin/usuarios"    element={<P pageId="admin-usuarios"    groups={G_ADMIN} userGroups={grps} abasPermitidas={abasPermitidas}><Admin initialTab="usuarios" /></P>} />
          <Route path="/admin/permissoes"  element={<P pageId="admin-permissoes"  groups={G_ADMIN} userGroups={grps} abasPermitidas={abasPermitidas}><AdminPermissoes /></P>} />
          <Route path="/admin/dados-tecnicos" element={<P pageId="admin-dados-tecnicos" groups={G_ADMIN} userGroups={grps} abasPermitidas={abasPermitidas}><AdminDadosTecnicos /></P>} />
          <Route path="/admin/codigos-convite" element={<P pageId="admin-codigos-convite" groups={G_ADMIN} userGroups={grps} abasPermitidas={abasPermitidas}><AdminCodigosConvite /></P>} />
          <Route path="/admin/perfis"      element={<P pageId="admin-perfis"      groups={G_ADMIN} userGroups={grps} abasPermitidas={abasPermitidas}><Admin initialTab="perfis" /></P>} />
          <Route path="/admin/hierarquia"  element={<P pageId="admin-hierarquia"  groups={G_ADMIN} userGroups={grps} abasPermitidas={abasPermitidas}><AdminHierarquia /></P>} />
          <Route path="/admin/d4sign"      element={<P pageId="admin-d4sign"      groups={G_ADMIN} userGroups={grps} abasPermitidas={abasPermitidas}><Admin initialTab="d4sign" /></P>} />
          <Route path="/admin/empresa"     element={<P pageId="admin-empresa"     groups={G_ADMIN} userGroups={grps} abasPermitidas={abasPermitidas}><Admin initialTab="empresa" /></P>} />
          <Route path="/admin/custos-aws"  element={<P pageId="admin-aws-costs"   groups={G_ADMIN} userGroups={grps} abasPermitidas={abasPermitidas}><Admin initialTab="aws-costs" /></P>} />
          <Route path="/admin/backup-nas"  element={<P pageId="admin-backup-nas"  groups={G_ADMIN} userGroups={grps} abasPermitidas={abasPermitidas}><Admin initialTab="backup-nas" /></P>} />
          <Route path="/admin/integracoes" element={<P pageId="admin-integracoes" groups={G_ADMIN} userGroups={grps} abasPermitidas={abasPermitidas}><AdminIntegracoes /></P>} />
          <Route path="/admin/backlog"     element={<P pageId="admin-backlog"     groups={G_ADMIN} userGroups={grps} abasPermitidas={abasPermitidas}><Admin initialTab="backlog" /></P>} />

          {/* ─── Meu perfil ─── */}
          <Route path="/perfil" element={<MeuPerfil />} />

          {/* ─── Fallback: qualquer URL desconhecida volta pra home ─── */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>

        {/* ─── Drawers contextuais por módulo ─── */}
        {moduloAtivo === 'comercial' && (grps.includes('admin') || grps.includes('comercial')) && (
          <HydraDrawer />
        )}
        {moduloAtivo === 'operacional' && (grps.includes('admin') || grps.includes('operacional') || grps.includes('diretoria')) && (
          <JimDrawer />
        )}
        {moduloAtivo === 'administrativo' && (grps.includes('admin') || grps.includes('administrativo') || grps.includes('financeiro') || grps.includes('financeiro_editor')) && (
          <PamDrawer />
        )}

        {/* ─── Banner PWA: aparece quando o SW detecta nova versao ─── */}
        <BannerAtualizacaoSW />
      </AppShell.Main>
    </AppShell>
  );
}

// ─── Atalho para ProtectedRoute (encurta as declarações de rota) ─────
function P({ pageId, groups, userGroups, abasPermitidas, children }: {
  pageId: Pagina;
  groups: string[];
  userGroups: string[];
  abasPermitidas: string[] | null;
  children: ReactNode;
}) {
  return (
    <ProtectedRoute
      pageId={pageId}
      groupsFallback={groups}
      userGroups={userGroups}
      abasPermitidas={abasPermitidas}
    >
      {children}
    </ProtectedRoute>
  );
}

// ─── Tela "sem acesso" extraída pra fora do shell ─────
function TelaSemAcesso({ signOut, grps, user, debugInfo }: any) {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: HD_BG }}>
      <div style={{ background: '#fff', padding: 40, borderRadius: 12, textAlign: 'center', maxWidth: 420, boxShadow: '0 2px 20px rgba(0,0,0,0.06)' }}>
        <div style={{ fontSize: 40 }}>🔒</div>
        <h3 style={{ color: HD_BLUE_DEEP, marginTop: 14 }}>Acesso não autorizado</h3>
        <p style={{ fontSize: 13, color: '#666', marginBottom: 14 }}>
          Seu usuário ainda não tem nenhum perfil de acesso atribuído.
          Se isso é um erro, peça ao administrador para revisar seus grupos e faça login novamente.
        </p>
        <div style={{ fontSize: 11, color: '#aaa', marginBottom: 16 }}>
          Dica: se seus grupos foram alterados recentemente, sair e entrar de novo atualiza suas permissões.
        </div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
          <button onClick={signOut} style={{
            padding: '10px 22px', fontSize: 13, borderRadius: 6,
            border: 'none', background: HD_BLUE, color: '#fff', cursor: 'pointer', fontWeight: 600,
          }}>
            Sair e entrar novamente
          </button>
          <button onClick={async () => {
            try {
              localStorage.clear();
              sessionStorage.clear();
              for (const c of document.cookie.split(';')) {
                const n = c.split('=')[0].trim();
                document.cookie = `${n}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
              }
            } catch {}
            window.location.reload();
          }} style={{
            padding: '10px 22px', fontSize: 13, borderRadius: 6,
            border: '0.5px solid #B91626', background: '#fff', color: '#B91626', cursor: 'pointer', fontWeight: 600,
          }}>
            Limpar sessão e recarregar
          </button>
        </div>

        <details style={{ marginTop: 20, textAlign: 'left' }}>
          <summary style={{ fontSize: 11, color: '#aaa', cursor: 'pointer' }}>Informações técnicas (envie ao admin)</summary>
          <pre style={{
            marginTop: 8, padding: 10, background: '#F0F1F5', borderRadius: 4,
            fontSize: 10, color: '#555', overflow: 'auto', maxHeight: 240,
          }}>{JSON.stringify({ grupos_detectados: grps, email_autenticador: user?.signInDetails?.loginId, ...debugInfo }, null, 2)}</pre>
        </details>
      </div>
    </div>
  );
}

export default App;
